import { inject, Injectable, isDevMode, OnDestroy } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentStore, OnStoreInit, tapResponse } from '@ngrx/component-store';
import {
	AccessLevel,
	AccessLevels,
	AppStateService,
	extractOptions,
	UserAccessService,
} from '@siza-staff-care/shared/app-state';
import {
	AdminController,
	AdminScreenConfigDto,
	AggregateResultDto,
	ApiDtoModel,
	CollHolder,
	decode,
	EditDto,
	InfoDto,
	isNewOid,
	LookupType,
	obfuscate,
	obfuscateNew,
	ScreenConfigDto,
} from '@siza-staff-care/shared/data';
import { GenericDialogsService, SaveChangesResult } from '@siza-staff-care/shared/generic-dialogs';
import { GenericErrorHandlerService } from '@siza-staff-care/shared/global-errors';
import {
	AuthStore,
	ControlSecurityService,
	CrudService,
	EditFlags_Create,
	EditFlags_Update,
	EditorSecurityService,
	getAccessLevelForCurrentScreen,
} from '@siza-staff-care/shared/global-services';
import { SnackbarService } from '@siza-staff-care/shared/ui-components/common-ui';
import {
	AllowedEditButtons,
	EditButtonsVm,
	EditListVm,
	GlobalStoreRegistryStore,
	IStore,
	IStore_Internal,
	RouteToRootOptions,
	StoreRegistry,
} from '@siza-staff-care/shared/ui-components/store-registry';
import { assertDefined, diff, makeObservableBool, typedClone } from '@siza-staff-care/shared/utils';
import { NgEventBus } from 'ng-event-bus';
import {
	catchError,
	combineLatest,
	debounceTime,
	defer,
	delay,
	EMPTY,
	exhaustMap,
	filter,
	finalize,
	from,
	map,
	mergeMap,
	Observable,
	of,
	startWith,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';
import { ICrudAdaptor } from './crud-adaptor-interface';
import { navigateRouterToSecondaryOutlet, navigateSecondary } from './secondary-outlet-navigator';
import { StandardCrudAdaptor } from './standard-crud-adaptor';

const DEBUG = false;
const DEBUG_DIFF = false;
const DEBUG_CHANGES = false;

export interface GenericEditState<
	TEdit extends EditDto,
	TInfo extends InfoDto,
	TAdditionalState extends object,
	TController extends AdminController,
> {
	createOid: string | undefined;
	oidObfuscated: string | undefined;
	list: TInfo[];
	edit: TEdit | undefined;
	dtos: unknown[] | undefined;
	priorEdit: TEdit | undefined;
	loadingList: boolean;
	loadingEdit: boolean;
	hasUnsavedChanges: boolean;
	isNewInstance: boolean;
	isEditing: boolean;
	form: UntypedFormGroup | undefined;
	formRawValue: unknown | undefined;
	screenConfig: AdminScreenConfigDto | undefined;
	controllerParams?: TController;
	loadCount?: number;
	selectCount: number;
	allowedEditButtons: AllowedEditButtons;
	isActive: boolean;
	isAlwaysSavable: boolean;
	url?: string;
	activatedRoute?: ActivatedRoute;
	operationInProgress?: boolean;
	screenAccessLevel?: AccessLevel;
	confirmWarningNextSave?: boolean;
	insteadOfSelect?: TEdit;
	formPending: boolean;
	forcingClear: boolean;
	ignoreClear: boolean;
	preventClearNewDelete: boolean;
	hasOpened: boolean;
	forceOnEditSetForNext?: boolean;
	suppressHandleLoadError?: boolean;
	canSaveOverride?: boolean;
	warnings?: string[];
	additionalState?: TAdditionalState;
}

const defaultState = {
	createOid: undefined,
	oidObfuscated: undefined,
	list: [],
	edit: undefined,
	dtos: undefined,
	priorEdit: undefined,
	loadingList: false,
	loadingEdit: false,
	hasUnsavedChanges: false,
	isNewInstance: false,
	isEditing: false,
	form: undefined,
	formRawValue: undefined,
	screenConfig: undefined,
	isActive: true,
	isAlwaysSavable: false,
	selectCount: 0,
	formPending: false,
	forcingClear: false,
	ignoreClear: false,
	preventClearNewDelete: false,
	hasOpened: false,
	operationInProgress: false,
	allowedEditButtons: {
		allowNew: true,
		allowDelete: true,
	},
};

export interface SaveResult<T extends EditDto> extends AggregateResultDto {
	dtos: [T, CollHolder];
}

@Injectable()
export abstract class GenericEditStore<
		TEdit extends EditDto,
		TInfo extends InfoDto = TEdit,
		TAdditionalState extends object = Record<string, never>,
		TController extends AdminController = AdminController,
	>
	extends ComponentStore<GenericEditState<TEdit, TInfo, TAdditionalState, TController>>
	implements IStore, OnStoreInit, OnDestroy
{
	protected _dialogs = inject(GenericDialogsService);
	protected _crud = inject(CrudService);
	protected _snackbar = inject(SnackbarService);
	protected _router = inject(Router);
	protected _eventBus = inject(NgEventBus);
	protected _genericErrorHandler = inject(GenericErrorHandlerService);
	protected _appState = inject(AppStateService);
	protected _userAccess = inject(UserAccessService);
	protected _crudAdaptor: ICrudAdaptor = inject(StandardCrudAdaptor);
	protected _controlSecurity = inject(ControlSecurityService);
	private _globalRegistry = inject(GlobalStoreRegistryStore);
	private _auth = inject(AuthStore);
	protected editorSecurity = inject(EditorSecurityService);

	protected storeRegistry? = inject(StoreRegistry, { optional: true });

	constructor() {
		super(defaultState);

		this.setAllowedEditButtons({});
		this.registerStoreOnNextTick();
	}

	//
	// Plain properties
	//
	lookupTypes: LookupType[] = [];
	abstract niceName: string;
	abstract controllerType: string;
	tabName?: string;
	storeKey?: string;
	saveButtonLabel = 'Save';
	protected ignoreEditOid = false;
	protected replaceEditUrl = false;
	protected allowRerouteToEdit = false;
	/**
	 * Allows overriding the complete text used in the unsaved changes dialog.
	 */
	protected saveChangesOverrideText = '';

	protected options: Record<string, unknown> = {};

	get canRouteToCreateNew() {
		return !!this.get().activatedRoute;
	}

	private readonly subCancelSelection = new Subject<string | undefined>();
	readonly cancelSelection$ = this.subCancelSelection.asObservable();

	//
	// Selectors
	//
	readonly list$ = this.select(({ list }) => list);
	readonly edit$ = this.select(({ edit }) => edit);
	readonly dtos$ = this.select(({ dtos }) => dtos);
	readonly priorEdit$ = this.select(({ priorEdit }) => priorEdit);
	readonly hasEdit$ = this.select(({ edit }) => !!edit, { debounce: true });
	readonly loadingList$ = this.select(({ loadingList }) => loadingList);
	readonly loadingEdit$ = this.select(({ loadingEdit }) => loadingEdit);
	readonly isEditing$ = this.select(({ isEditing }) => isEditing, { debounce: true });
	readonly hasUnsavedChanges$ = this.select(({ hasUnsavedChanges }) => hasUnsavedChanges);
	readonly canDelete$ = this.select(({ edit, isNewInstance }) => !!edit && !isNewInstance);
	readonly canClearNewDelete$ = this.select(({ preventClearNewDelete }) => !preventClearNewDelete);
	readonly inProgress$ = this.select((state) => state.loadingEdit || state.loadingList);
	readonly warnings$ = this.select(({ warnings }) => warnings || []);
	readonly hasWarnings$ = this.select(this.warnings$, (warnings) => warnings?.length > 0);

	readonly form$ = this.select(({ form }) => form);
	readonly formRawValue$ = this.select(({ formRawValue }) => formRawValue);
	readonly isNewInstance$ = this.select(({ isNewInstance }) => isNewInstance);
	readonly isExistingInstance$ = this.select(({ isNewInstance }) => !isNewInstance);
	readonly screenConfig$ = this.select(({ screenConfig }) => screenConfig);
	readonly allowedEditButtons$ = this.select(({ allowedEditButtons }) => allowedEditButtons);
	readonly activatedRoute$ = this.select(({ activatedRoute }) => activatedRoute);

	readonly loadCount$ = this.select(({ loadCount }) => loadCount, { debounce: true });
	readonly screenReadOnly$ = this.select(({ screenAccessLevel }) => screenAccessLevel === AccessLevels.ReadOnly);

	readonly additionalState$ = this.select(({ additionalState }) => additionalState);

	readonly obfuscatedOid$ = this.select(({ oidObfuscated }) => oidObfuscated);
	readonly controllerParams$ = this.select(({ controllerParams }) => controllerParams);
	readonly isActive$ = this.select(({ isActive }) => isActive);
	readonly isAlwaysSavable$ = this.select(({ isAlwaysSavable }) => isAlwaysSavable);
	readonly operationInProgress$ = this.select(({ operationInProgress }) => operationInProgress);
	readonly hasNonNewEdit$ = this.select(
		this.edit$,
		this.isNewInstance$,
		(edit, isNewInstance) => !!(edit && !isNewInstance),
	);
	readonly isClear$ = this.select(this.edit$, (edit) => !edit);
	readonly confirmWarningNextSave$ = this.select(({ confirmWarningNextSave }) => !!confirmWarningNextSave);

	readonly isSavable$ = this.select(
		this.hasUnsavedChanges$,
		this.isAlwaysSavable$,
		(hasUnsavedChanges, isAlwaysSavable) => hasUnsavedChanges || isAlwaysSavable,
	);
	readonly formPending$ = this.select(({ formPending }) => formPending);
	readonly opened$ = this.select(({ hasOpened }) => hasOpened);
	readonly preventClearNewDelete$ = this.select(this.canClearNewDelete$, (canClearNewDelete) => !canClearNewDelete);
	readonly suppressHandleLoadError$ = this.select(({ suppressHandleLoadError }) => !!suppressHandleLoadError);
	readonly canSaveOverride$ = this.select(({ canSaveOverride }) => canSaveOverride);
	protected forceAllowSaveOverride$: Observable<boolean | undefined> = of(undefined);

	readonly allowedEditButtons_hideAll$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.hideAll)),
	);

	readonly allowedEditButtons_allowNew$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowNew, true)),
	);

	readonly allowedEditButtons_allowSave$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowSave, true)),
	);

	readonly allowedEditButtons_allowRestore$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowRestore, true)),
	);

	readonly allowedEditButtons_allowDelete$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowDelete, true)),
	);

	readonly allowedEditButtons_allowClear$ = this.allowedEditButtons$.pipe(
		switchMap((allowedEditButtons) => makeObservableBool(allowedEditButtons.allowClear, true)),
	);

	/**
	 * Define a selector to determine whether the user can save the current edit, taking into account whether
	 * it's dirty, and if they're allowed to save by whether the save button is allowed, and also their security
	 * settings (editor flags) for this store.
	 */
	readonly allowSave$ = this.select(
		this.allowedEditButtons_hideAll$,
		this.allowedEditButtons_allowSave$,
		this.hasUnsavedChanges$,
		this.canSaveOverride$,
		this.isNewInstance$,
		(hideAll, allowSave, hasUnsavedChanges, canSaveOverride, isNewInstance) => {
			if (hideAll || !allowSave || !hasUnsavedChanges) {
				return false;
			}

			// Check security
			const editFlags = this.editorSecurity.getEditorSecurity(this);
			const canSave = isNewInstance ? !!(editFlags & EditFlags_Create) : !!(editFlags & EditFlags_Update);

			if (canSaveOverride !== undefined) {
				return canSaveOverride && canSave;
			}

			return canSave;
		},
	);

	readonly allowSave_EditorFlags$ = this.select(
		this.allowedEditButtons_hideAll$,
		this.allowedEditButtons_allowSave$,
		this.canSaveOverride$,
		this.isNewInstance$,
		(hideAll, allowSave, canSaveOverride, isNewInstance) => {
			if (hideAll || !allowSave) {
				return false;
			}

			// Check security
			const editFlags = this.editorSecurity.getEditorSecurity(this);
			const canSave = isNewInstance ? !!(editFlags & EditFlags_Create) : !!(editFlags & EditFlags_Update);

			if (canSaveOverride !== undefined) {
				return canSaveOverride && canSave;
			}

			return canSave;
		},
	);

	private readonly selectCount$ = this.select(({ selectCount }) => selectCount);
	private readonly selectedOid$ = this.select(({ createOid, edit, isNewInstance }) => {
		let oid: string | undefined;
		if (edit) {
			if (isNewInstance) {
				oid = createOid !== undefined ? createOid + '' : undefined;
			} else {
				oid = edit.oid || '';
			}
		}

		return oid;
	});

	readonly hasOpened$ = this.select(({ hasOpened }) => !!hasOpened);

	readonly realOid$ = combineLatest([this.selectedOid$, this.selectCount$]).pipe(
		map(([realOid]) => {
			realOid = realOid || '';
			return realOid;
		}),
	);

	protected isScreenReadonly$() {
		return defer(() => {
			return of(this.isScreenReadonly);
		});
	}

	get isScreenReadonly() {
		const access = getAccessLevelForCurrentScreen(this._router, this._userAccess);
		return access === AccessLevels.ReadOnly;
	}

	private readonly insteadOfSelect$ = this.select(({ insteadOfSelect }) => insteadOfSelect);
	readonly ignoreClear$ = this.select(({ ignoreClear }) => ignoreClear);

	readonly forceOnEditSetForNext$ = this.select(({ forceOnEditSetForNext }) => forceOnEditSetForNext);

	private subSaved = new Subject<boolean>();
	readonly saved$ = this.subSaved.asObservable();

	private subDeleted = new Subject<boolean>();
	readonly deleted$ = this.subDeleted.asObservable();

	private subEditOrCreateComplete = new Subject<boolean>();
	editOrCreateComplete$ = this.subEditOrCreateComplete.asObservable();

	private niceName$ = defer(() => of(this.niceName));
	private saveButtonLabel$ = defer(() => of(this.saveButtonLabel));

	readonly buttonsVm$: Observable<EditButtonsVm> = this.select({
		hideAll: this.allowedEditButtons_hideAll$,
		allowNew: this.allowedEditButtons_allowNew$,
		allowSave: this.allowedEditButtons_allowSave$,
		allowRestore: this.allowedEditButtons_allowRestore$,
		allowDelete: this.allowedEditButtons_allowDelete$,
		allowClear: this.allowedEditButtons_allowClear$,

		allowSave_EditorFlags: this.allowSave_EditorFlags$,

		hasEdit: this.hasEdit$,
		isSavable: this.isSavable$,
		formPending: this.formPending$,
		hasUnsavedChanges: this.hasUnsavedChanges$,
		isNewInstance: this.isNewInstance$,
		canDelete: this.canDelete$,
		canClearNewDelete: this.canClearNewDelete$,

		inProgress: this.inProgress$,

		niceName: this.niceName$,
		saveButtonLabel: this.saveButtonLabel$,
	});

	readonly editListVm$: Observable<EditListVm> = this.select({
		list: this.list$,
		edit: this.edit$,
		realOid: this.realOid$,
		loadingList: this.loadingList$,
		loadingEdit: this.loadingEdit$,
		isEditing: this.isEditing$,
		loadCount: this.loadCount$,
		hasOpened: this.hasOpened$,
		hasEdit: this.hasEdit$,
	});

	readonly editVm$ = this.select({
		edit: this.edit$,
		loadingEdit: this.loadingEdit$,
		inProgress: this.inProgress$,
	});

	get isForcingClear() {
		return this.get().forcingClear;
	}

	/**
	 * Synchronously gets whether the current edit is new or not.
	 */
	get isNewInstance() {
		const { isNewInstance } = this.get();
		return isNewInstance;
	}

	/**
	 * Synchronously gets the list.
	 */
	get list() {
		const { list } = this.get();
		return list;
	}

	/**
	 * Synchronously returns the obfuscated oid.
	 */
	get oidObfuscated() {
		return this.get().oidObfuscated;
	}

	/**
	 * Synchronously returns the decoded version of the obfuscated oid.
	 */
	get oidDecoded() {
		if (this.oidObfuscated) {
			return decode(this.oidObfuscated);
		}

		return '';
	}

	get isReadOnly() {
		return this._controlSecurity.isCurrentScreenReadOnly();
	}

	get hasOpened() {
		return this.get().hasOpened;
	}

	get activatedRoute() {
		return this.get().activatedRoute;
	}

	//
	// Updaters
	//

	protected readonly patchAdditional = this.updater((state, additional: Partial<TAdditionalState>) => {
		return {
			...state,
			additionalState: {
				...state.additionalState,
				...additional,
			} as TAdditionalState,
		};
	});

	/**
	 * Allows setting an "edit" object directly, bypassing the CRUD select from the server.
	 */
	protected readonly setInsteadOfSelect = this.updater((state, insteadOfSelect: TEdit | undefined) => ({
		...state,
		insteadOfSelect,
	}));

	readonly setList = this.effect((list$: Observable<TInfo[] | null>) =>
		list$.pipe(
			tap((list) => {
				this.onListSet(list);
				this.patchState({ list: list || [] });
			}),
		),
	);

	readonly setDtos = this.updater((state, dtos: unknown[] | undefined) => {
		return {
			...state,
			dtos,
		};
	});

	readonly setLoadingList = this.updater((state, loadingList: boolean) => ({
		...state,
		loadingList,
	}));

	readonly setLoadingEdit = this.updater((state, loadingEdit: boolean) => ({
		...state,
		loadingEdit,
	}));

	readonly setIsEditing = this.updater((state, isEditing: boolean) => ({
		...state,
		isEditing,
	}));

	readonly setIsNewInstance = this.updater((state, isNewInstance: boolean) => ({
		...state,
		isNewInstance,
	}));

	readonly setForm = this.updater((state, form: UntypedFormGroup | undefined) => ({
		...state,
		form,
	}));

	readonly setCreateOid = this.updater((state, createOid: string | undefined) => ({
		...state,
		createOid,
	}));

	readonly setScreenConfig = this.updater((state, screenConfig: AdminScreenConfigDto | undefined) => ({
		...state,
		screenConfig,
	}));

	readonly bumpLoadCount = this.updater((state) => {
		return {
			...state,
			loadCount: state.loadCount ? state.loadCount + 1 : 1,
		};
	});

	readonly setAllowedEditButtons = this.updater((state, allowedEditButtons: AllowedEditButtons) => {
		// Ensure the hideAll flag is clear if undefined to prevent buttons from being hidden
		// when hideAll isn't configured.
		if (allowedEditButtons.hideAll === undefined) {
			allowedEditButtons.hideAll = false;
		}

		return {
			...state,
			allowedEditButtons,
		};
	});

	readonly _setOidObfuscated = this.effect((oidObfuscated$: Observable<string | undefined>) =>
		oidObfuscated$.pipe(
			tap((oidObfuscated) => {
				this.patchState({ oidObfuscated });
			}),
		),
	);

	readonly setIsActive = this.updater((state, isActive: boolean) => ({
		...state,
		isActive,
	}));

	readonly setUrl = this.updater((state, url: string | undefined) => ({
		...state,
		url,
	}));

	readonly setIsAlwaysSavable = this.updater((state, isAlwaysSavable: boolean) => ({
		...state,
		isAlwaysSavable,
	}));

	readonly setOperationInProgress = this.updater((state, operationInProgress: boolean) => ({
		...state,
		operationInProgress,
	}));

	private readonly setForceOnEditSetForNext = this.updater((state, forceOnEditSetForNext: boolean) => ({
		...state,
		forceOnEditSetForNext,
	}));

	//
	// Effects
	//

	readonly overrideCanSave = this.effect((canSaveOverride$: Observable<boolean>) =>
		canSaveOverride$.pipe(
			tap((canSaveOverride) => {
				this.patchState({ canSaveOverride });
			}),
		),
	);

	readonly _setEdit = this.effect((edit$: Observable<TEdit | null>) =>
		edit$.pipe(
			withLatestFrom(this.edit$, this.forceOnEditSetForNext$),
			tap(([edit, oldEdit, forceOnEditSetForNext]) => {
				edit = this.transformEdit(edit);
				this.patchState({ edit: edit || undefined });
				if (this.ignoreEditOid || oldEdit?.oid !== edit?.oid || forceOnEditSetForNext || (edit && !edit.oid)) {
					this.onEditSet(edit);
				}

				this.patchState({
					priorEdit: edit ? typedClone(edit) : undefined,
					hasUnsavedChanges: false,
					forceOnEditSetForNext: false,
				});
			}),
		),
	);

	readonly setSuppressHandleLoadError = this.effect((suppressHandleLoadError$: Observable<boolean>) =>
		suppressHandleLoadError$.pipe(
			tap((suppressHandleLoadError) => {
				this.patchState({
					suppressHandleLoadError,
				});
			}),
		),
	);

	readonly setUpdatedEdit = this.effect((edit$: Observable<TEdit>) =>
		edit$.pipe(
			tap((edit) => {
				if (edit) {
					edit.ed = (edit.ed ?? 0) + 1;
				}
				this.patchState({
					edit,
					hasUnsavedChanges: this.protectUnsavedChangesFromBecomingTrueOnReadOnlyScreen(true),
				});
			}),
		),
	);

	readonly setUpdatedEditNoChanges = this.effect((edit$: Observable<TEdit>) =>
		edit$.pipe(
			tap((edit) => {
				if (edit) {
					edit.ed = (edit.ed ?? 0) + 1;
				}
				this.patchState({
					edit,
				});
			}),
		),
	);

	private readonly registerStoreOnNextTick = this.effect((trigger$) =>
		trigger$.pipe(
			delay(0),
			tap(() => {
				this._globalRegistry.registerStore(this);
			}),
		),
	);

	readonly ignoreNextClear = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.setIgnoreClear(true);
			}),
		),
	);

	private readonly setIgnoreClear = this.effect((ignoreClear$: Observable<boolean>) =>
		ignoreClear$.pipe(
			tap((ignoreClear) => {
				this.patchState({ ignoreClear });
			}),
		),
	);

	readonly registerActivatedRoute = this.effect((route$: Observable<ActivatedRoute>) =>
		route$.pipe(
			tap((route) => {
				this.patchState({
					activatedRoute: route,
				});
			}),
		),
	);

	readonly setControllerParameters = this.effect((controllerParams$: Observable<TController>) => {
		return controllerParams$.pipe(
			tap((controllerParams) => {
				this.patchState({
					controllerParams,
				});
			}),
		);
	});

	readonly registerForm = this.effect((form$: Observable<UntypedFormGroup | undefined>) => {
		return form$.pipe(
			switchMap((form) => {
				this.setForm(form);

				if (form) {
					return form.valueChanges.pipe(
						debounceTime(50),
						tap(() => {
							const rawValue = form.getRawValue();
							const isDirty = form.dirty;

							this.patchState({ formRawValue: rawValue });
							this.setHasUnsavedChanges(isDirty);
						}),
					);
				}

				return EMPTY;
			}),
		);
	});

	/**
	 * Force clearing the edit. Used by the deactivate guard when successfully routed.
	 * Just calls a method which can be overridden to handle clearing the edit.
	 *
	 * Implemented for staff-records to force clearing the selected staff master when
	 * re-navigated to from the menu.
	 */
	readonly forceClear = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({ forcingClear: true });
				this.onForceClear();
				this.patchState({ forcingClear: false });
			}),
		),
	);

	readonly open = this.effect((trigger$) => {
		return trigger$.pipe(
			tap(() => {
				this.setLoadingList(true);
			}),
			withLatestFrom(this.controllerParams$),
			exhaustMap(([, controllerParams]) => {
				return this._crudAdaptor
					.open<AdminScreenConfigDto>(this.controllerType, this.lookupTypes, controllerParams)
					.pipe(
						tapResponse(
							(result: AdminScreenConfigDto) => {
								const { objects, options } = result;

								const list = objects;
								this.setList(list);
								this.setScreenConfig(result);

								this.options = extractOptions(options);

								this.afterOpen(result);
								this.patchState({ hasOpened: true });
							},
							(err) => this._genericErrorHandler.handleError(err, this.niceName, 'Opening'),
						),
						finalize(() => this.setLoadingList(false)),
					);
			}),
		);
	});

	readonly editOrCreate = this.effect((obfuscatedOid$: Observable<string>) =>
		obfuscatedOid$.pipe(
			withLatestFrom(this.form$, this.suppressHandleLoadError$),
			// Note: using exhaustMap rather than switchMap as we don't want to overwhelm Jade's limited supply of processes
			// with cancelled requests.
			exhaustMap(([obfuscatedOid, form, suppressHandleLoadError]) => {
				if (this.vetoSelect()) {
					this.signalEditOrCreateComplete(false);
					return EMPTY;
				}

				this.setOidObfuscated(obfuscatedOid);

				const decoded = decode(obfuscatedOid);
				const decodedAsNumber = Number(decoded);
				if (isNaN(decodedAsNumber)) {
					throw new Error('Invalid oid');
				}

				if (DEBUG) /* eslint-disable */console.log(...oo_oo(`548382022_821_15_821_89_4`,`${this.niceName} editOrCreate ${obfuscatedOid} = ${decoded}`));

				const isCreate = isNewOid(obfuscatedOid);

				const handleResponse = ({ dtos, warnings }: { dtos: TEdit[]; warnings: string[] }) => {
					const edit = dtos[0];

					this.patchState({ warnings: warnings || [] });

					this.setDtos(dtos);
					this.setEdit(edit);
					isCreate ? this.setIsNewInstance(true) : this.setCreateOid(undefined);
					this.bumpLoadCount();

					this.setCreateOid(undefined);

					setTimeout(() => {
						form?.markAsPristine();
						form?.markAsUntouched();
					});

					this.setHasUnsavedChanges(isCreate);
					this.afterEditSet(edit);
					this.onDtosSet(dtos);

					this.signalEditOrCreateComplete(true);
				};

				const handleError = (err) => {
					if (!suppressHandleLoadError) {
						this._genericErrorHandler.handleError(err, this.niceName, 'Getting');
					}
					this.setIsEditing(false);

					this.signalEditOrCreateComplete(false);
				};

				if (isCreate) {
					this.setCreateOid(decoded);

					this.setLoadingEdit(true);
					this.setIsEditing(true);

					return this.onGetCreateParams().pipe(
						// Opportunity to get some additional parameters for the create.
						exhaustMap((createParams) => {
							return this._crudAdaptor.create(this.controllerType, createParams).pipe(
								tapResponse(handleResponse, handleError),
								finalize(() => this.setLoadingEdit(false)),
							);
						}),
					);
				} else {
					// It's a Jade instance OID.
					this.setLoadingEdit(true);
					this.setIsEditing(true);

					return this.onGetSelectParams().pipe(
						withLatestFrom(this.insteadOfSelect$),
						// Opportunity to get some additional parameters for the select.
						exhaustMap(([selectParams, insteadOfSelect]) => {
							if (insteadOfSelect) {
								this.setInsteadOfSelect(undefined);

								this.setLoadingEdit(false);
								this.setEdit(insteadOfSelect);
								this.setIsEditing(true);

								return EMPTY;
							}

							return this._crudAdaptor.select(this.controllerType, obfuscatedOid, selectParams).pipe(
								tapResponse(handleResponse, handleError),
								finalize(() => {
									this.setLoadingEdit(false);
								}),
							);
						}),
					);
				}
			}),
		),
	);

	readonly restore = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.form$),
			tap(([, form]) => {
				const prior = this.get().priorEdit;
				if (prior) {
					this.setEdit(prior);
				}

				form?.markAsUntouched();
				this.onRestore(prior);

				setTimeout(() => {
					form?.markAsUntouched();
					form?.markAsPristine();

					this.setHasUnsavedChanges(false);
				});
			}),
		),
	);

	private preventMutatingActionWhilePaintingSecurity(actionType: string): Observable<void> {
		return this._appState.isPaintingSecurity$.pipe(
			take(1),
			delay(0), // so the signalSaveSuccess(false) happens on the next tick to allow the observable to be subscribed to first.
			switchMap((isPaintingSecurity) => {
				if (isPaintingSecurity) {
					this._dialogs.message({
						title: `Can't ${actionType}`,
						message: `${actionType} is prohibited while painting security.`,
					});

					this.signalSaveSuccess(false);
					return EMPTY;
				}

				return of(undefined);
			}),
		);
	}

	/**
	 * Drives the router to the edit UI for the given obfuscated oid.
	 */
	readonly routeToEdit = this.effect((oid$: Observable<string>) =>
		oid$.pipe(
			withLatestFrom(this.activatedRoute$, this.edit$),
			switchMap(([oid, route]) => {
				if (this.oidObfuscated === oid && this.allowRerouteToEdit) {
					this.refreshCurrentEdit();
					return of(true);
				}

				return from(
					this._router.navigate([oid], {
						relativeTo: route,
						replaceUrl: this.replaceEditUrl,
						queryParamsHandling: 'merge',
						skipLocationChange: true,
						state: {
							// Set a flag to prevent the store-aware-can-deactivate guard from re-routing to the base URL.
							preventDeactivateReroute: true,
						},
					}),
				).pipe(
					tap((routedOk) => {
						if (routedOk) {
							this.setOidObfuscated(oid);
							this.onRouted();
						} else {
							if (oid) {
								const lastSelectedId = this.get().oidObfuscated;
								this.subCancelSelection.next(lastSelectedId);
							}
						}
					}),
				);
			}),
		),
	);

	/**
	 * Drives the router to the edit UI for the given real (non-obfuscated) oid.
	 */
	readonly routeToEditReal = this.effect((oid$: Observable<string | undefined>) =>
		oid$.pipe(
			tap((oid) => {
				if (this.oidDecoded === oid && !this.allowRerouteToEdit) {
					return;
				}

				oid = obfuscate(oid ?? '');
				this.routeToEdit(oid);
			}),
		),
	);

	/**
	 * Drives the router to the edit UI for the given real (non-obfuscated) oid, setting the replaceUrl flag to true.
	 */
	readonly routeToEditReal_replaceUrl = this.effect((oid$: Observable<string | undefined>) =>
		oid$.pipe(
			tap((oid) => {
				const oldReplace = this.replaceEditUrl;
				this.replaceEditUrl = true;
				this.routeToEditReal(oid);
				this.replaceEditUrl = oldReplace;
			}),
		),
	);

	/**
	 * Drives the router to the edit UI for a new instance.
	 */
	readonly routeToCreateNew = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => {
				// An opportunity to have a custom create new (for new starter workflow)
				return this.onInsteadOfCreate();
			}),
			withLatestFrom(this.activatedRoute$),
			tap(([, activatedRoute]) => {
				assertDefined(activatedRoute, 'No activatedRoute - cannot use navigateToCreateNew');
				const oid = obfuscateNew();
				this._router.navigate([oid], {
					replaceUrl: true,
					relativeTo: activatedRoute,
					queryParamsHandling: 'merge',
				});
			}),
		),
	);

	readonly setHasUnsavedChanges = this.effect((hasUnsavedChanges$: Observable<boolean>) =>
		hasUnsavedChanges$.pipe(
			withLatestFrom(this.edit$, this.priorEdit$, this.form$),
			tap(([hasUnsavedChanges, edit, priorEdit, form]) => {
				// Prevent unsaved changes if there's no edit.
				if (!edit) {
					hasUnsavedChanges = false;
				}

				hasUnsavedChanges = this.vetoHasUnsavedChanges(hasUnsavedChanges);

				if (isDevMode() && DEBUG_DIFF) {
					const difference = diff(edit, priorEdit);
					/* eslint-disable */console.log(...oo_oo(`548382022_1052_5_1052_63_4`,`Diff between edit and priorEdit`, difference));
				}

				if (hasUnsavedChanges) {
					form?.markAsDirty();
				}

				this.onHasUnsavedChangesSet(hasUnsavedChanges);

				// console.log(
				// 	`%c setHasUnsavedChanges: ${hasUnsavedChanges} for ${this.niceName}`,
				// 	`color: ${hasUnsavedChanges ? 'red' : 'green'}	`,
				// );

				this.patchState({ hasUnsavedChanges });
			}),
		),
	);

	private readonly _clear = this.effect((options$?: Observable<RouteToRootOptions | undefined>) =>
		(options$ || of(undefined)).pipe(
			withLatestFrom(this.activatedRoute$, this.ignoreClear$),
			switchMap(([options, route, ignoreClear]) => {
				const doClear = () => {
					this.setIsEditing(false);
					this.setDtos(undefined);
					this.setEdit(null);
					this.setIsNewInstance(false);
					this.setOidObfuscated(undefined);

					const fromButtons = false;
					this.afterClear(fromButtons);

					const lastSelectedId = this.get().oidObfuscated;
					if (lastSelectedId) {
						this.subCancelSelection.next(lastSelectedId);
					}

					this.onRouted();
				};

				if (!route) {
					doClear();
					return EMPTY;
				}

				if (this.storeRegistry && !options?.dontSetActiveStore) {
					this.storeRegistry.setActiveStore(this);
				}

				// Don't trigger routing if forcing clear - this is already triggered by routing,
				// so routing again will result in a double navigation - a NavigationCancel event.
				if (this.isForcingClear) {
					return of(true);
				}

				if (ignoreClear) {
					this.setIgnoreClear(false);
					return of(true);
				}

				return from(this._router.navigate(['.'], { relativeTo: route, queryParamsHandling: 'merge' })).pipe(
					tap((routedOk) => {
						if (routedOk !== false) {
							doClear();
						}
					}),
				);
			}),
		),
	);

	/**
	 * Clear without checking for unsaved changes.
	 */
	private readonly _cancel = this.effect((trigger$: Observable<void>) => {
		return trigger$.pipe(
			tap(() => {
				this.setIsEditing(false);
				this.setDtos(undefined);
				this.setEdit(null);
				this.setIsNewInstance(false);
				this.setOidObfuscated(undefined);

				this.afterClear();
			}),
		);
	});

	/**
	 * Clear without checking for unsaved changes.
	 */
	private readonly _destroy = this.effect((trigger$: Observable<void>) => {
		return trigger$.pipe(
			tap(() => {
				// Prevent navigating on destroy, which can trigger a NavigationCancel.
				this.setIgnoreClear(true);
				this._cancel();
				this.setIgnoreClear(false);
			}),
		);
	});

	readonly delete = this.effect((trigger$) => {
		return trigger$.pipe(
			switchMap(() => this.preventMutatingActionWhilePaintingSecurity('Delete')),
			withLatestFrom(this.edit$),
			exhaustMap(([, edit]) => {
				return this.showConfirmDeleteDialog(edit);
			}),
			withLatestFrom(this.realOid$),
			filter(([, realOid]) => realOid !== undefined),
			exhaustMap(([confirmDelete, realOid]) => {
				if (!confirmDelete) {
					this.signalDeleteSuccess(false);
					return EMPTY;
				}

				const deleteName = this.niceName;

				this.setLoadingEdit(true);
				return this.onGetDeleteParams().pipe(
					switchMap((deleteParams) =>
						this._crudAdaptor.delete(this.controllerType, realOid ?? '', deleteParams).pipe(
							tapResponse(
								(agResult) => {
									const result = agResult as SaveResult<TEdit>;
									const newList = result.dtos[1]?.objects || [];
									this.setDtos(undefined);
									this.setEdit(null);
									this.setList(newList);
									this.setOidObfuscated(undefined);

									this.signalDeleteSuccess(true);

									if (!this.suppressSnackbarMessages()) {
										this._snackbar.showMessage(`${deleteName} deleted.`);
									}

									this.onAfterSaveOrDelete(result.dtos);

									this.clear();
								},
								(err) => {
									this._genericErrorHandler.handleError(err, deleteName, 'Deleting');
									this.signalDeleteSuccess(false);
								},
							),
							finalize(() => this.setLoadingEdit(false)),
						),
					),
				);
			}),
		);
	});

	readonly deleteItem = this.effect((oid$: Observable<string>) =>
		oid$.pipe(
			exhaustMap(() => {
				return this._dialogs.showConfirmDeleteDialog(this.niceName, this.niceName.toLowerCase());
			}),
			withLatestFrom(oid$),
			exhaustMap(([confirmDelete, oid]) => {
				if (!confirmDelete) {
					return EMPTY;
				}

				this.setLoadingEdit(true);
				return this._crudAdaptor.delete(this.controllerType, oid ?? '').pipe(
					tapResponse(
						(agResult) => {
							const result = agResult as SaveResult<TEdit>;
							const newList = result.dtos[1]?.objects || [];
							this.setDtos(undefined);
							this.setEdit(null);
							this.setList(newList);
							this.setOidObfuscated(undefined);

							this._snackbar.showMessage(`${this.niceName} deleted.`);
						},
						(err) => this._genericErrorHandler.handleError(err, this.niceName, 'Deleting'),
					),
				);
			}),
		),
	);

	readonly saveWithConfirmWarning = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({ confirmWarningNextSave: true });
				this.save();
			}),
			finalize(() => {
				this.patchState({ confirmWarningNextSave: false });
			}),
		),
	);

	readonly showCantSaveMessage = this.effect((title$: Observable<string | undefined>) =>
		title$.pipe(
			tap((title) => {
				this._dialogs.showCantSaveMessage(title);
			}),
		),
	);

	readonly save = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => this.preventMutatingActionWhilePaintingSecurity('Save')),
			withLatestFrom(this.form$),
			switchMap(([, form]) => {
				if (!form) {
					this.saveOnceValid();
					return of(undefined);
				}

				return form.statusChanges.pipe(
					startWith(form.status),
					takeUntil(form.statusChanges.pipe(filter((status) => status !== 'PENDING'))),
					tap((status) => {
						if (status === 'PENDING') {
							this.patchState({ formPending: true });
						}
						this.saveOnceValid();
					}),
					finalize(() => {
						this.patchState({ formPending: false });
					}),
				);
			}),
		),
	);

	private readonly saveOnceValid = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.form$, this.edit$),
			delay(0),
			switchMap(([, form, edit]) => {
				if (form) {
					if (form.status === 'PENDING') {
						return EMPTY;
					}

					form.markAllAsTouched();
					form.updateValueAndValidity();

					if (form.invalid) {
						if (isDevMode()) {
							/* eslint-disable */console.log(...oo_oo(`548382022_1301_7_1301_24_4`,form));
						}

						this.showCantSaveMessage(`Can't save`);

						if (isDevMode()) {
							// For debugging - dump out the controls and their errors to the console.
							// TODO: Decide what to do with this. Should be be made available via the UI?
							const controlsAndErrors: Record<string, ValidationErrors> = Object.keys(
								form.controls,
							).reduce((acc, c) => {
								const ctrl = form.controls[c];
								const hasErrors = ctrl.errors && Object.keys(ctrl.errors).length > 0;
								if (hasErrors) {
									acc[c] = ctrl.errors;
								}

								return acc;
							}, {});

							/* eslint-disable */console.log(...oo_oo(`548382022_1321_7_1321_37_4`,controlsAndErrors));
						}

						this.signalSaveSuccess(false);
						return EMPTY;
					}
				}

				if (!edit) {
					throw new Error(`No edit (save) -  ${this.niceName} `);
				}

				return of(edit);
			}),
			switchMap((edit) => {
				// Opportunity to do something before saving.
				return this.preSave(edit);
			}),
			switchMap((edit) => {
				if (!edit) {
					this.signalSaveSuccess(false);
					return EMPTY;
				}

				return of(edit);
			}),
			switchMap((edit) => this.performSave(edit)),
		),
	);

	protected readonly handleSaveResult = this.effect((saveResult$: Observable<SaveResult<TEdit>>) =>
		saveResult$.pipe(
			withLatestFrom(this.form$),
			tapResponse(
				([agResult, form]) => {
					const result = agResult as SaveResult<TEdit>;

					this.patchState({ warnings: result.warnings || [] });

					if (!this.suppressSnackbarMessages()) {
						this._snackbar.showMessage(`${this.niceName} saved.`);
					}

					form?.markAsUntouched();
					form?.markAsPristine();

					if (!this.onHandleSaveResultAggregate(result)) {
						const newDto = result.dtos[0];
						const newList = result.dtos[1]?.objects || [];

						this.setIsNewInstance(false);
						this.setDtos(result.dtos);
						this.setEdit(newDto);
						this.setList(newList);

						this.onAfterSaveEdit(newDto);
						this.onAfterSaveOrDelete(result.dtos);
					}

					this.signalSaveSuccess(true);
				},
				(err) => {
					this._genericErrorHandler.handleError(err, this.niceName, 'Saving');
				},
			),
		),
	);

	readonly saveAndClear = this.effect((trigger$) => {
		let clearAfterSave = true;

		return trigger$.pipe(
			withLatestFrom(this.form$, this.edit$, this.allowedEditButtons$),
			delay(0),
			mergeMap(([, form, edit, allowedEditButtons]) => {
				clearAfterSave = !!allowedEditButtons.allowClear;

				if (form?.invalid) {
					form?.markAllAsTouched();

					this._dialogs.message('Please correct the errors', `Can't save`);
					return EMPTY;
				}
				assertDefined(edit);
				return of(edit);
			}),
			switchMap((edit) => {
				// Opportunity to do something before saving.
				return this.preSave(edit);
			}),
			switchMap((edit) => {
				if (!edit) {
					this.signalSaveSuccess(false);
					return EMPTY;
				}

				this.setLoadingEdit(true);

				return this.onGetSaveParams().pipe(
					switchMap((saveParams) =>
						this._crudAdaptor.save(this.controllerType, edit.ed ?? 0, edit.oid, edit, saveParams).pipe(
							tapResponse(
								(agResult) => {
									const result = agResult as SaveResult<TEdit>;

									const newList = result.dtos[1]?.objects || [];

									this.setIsEditing(false);
									this.setList(newList);

									if (clearAfterSave) {
										this.setDtos(undefined);
										this.setEdit(null);
										this.setForm(undefined);
										this.setOidObfuscated(undefined);
									}

									this._snackbar.showMessage(`${this.niceName} saved.`);

									this.onAfterSaveOrDelete(result.dtos);
									this.signalSaveSuccess(true);
								},
								(err) => {
									this._genericErrorHandler.handleError(err, this.niceName, 'Saving');
									this.signalSaveSuccess(false);
								},
							),
							finalize(() => this.setLoadingEdit(false)),
						),
					),
				);
			}),
		);
	});

	readonly copyCurrent = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.form$, this.edit$),
			exhaustMap(([, form, edit]) => {
				assertDefined(edit);

				const copy = {
					...edit,
					oid: '',
				};

				this.onCopyCurrent(copy);

				this.setEdit(copy);
				this.setIsNewInstance(true);

				this.bumpLoadCount();
				this.cancelAndClearChildStores();

				form?.reset();
				form?.markAsDirty();

				this.afterEditSet(edit);

				return EMPTY;
			}),
		),
	);

	readonly updateEdit = this.effect((updatedEdit$: Observable<TEdit>) =>
		updatedEdit$.pipe(
			withLatestFrom(this.form$),
			tap(([dto, form]) => {
				this.setUpdatedEdit(dto);
				form && form.markAsDirty();
			}),
		),
	);

	readonly markDirty = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.edit$),
			tap(([, edit]) => {
				if (!edit) {
					return;
				}
				this.updateEdit(edit);
			}),
		),
	);

	readonly handleGenericError = this.effect((error$: Observable<unknown>) => {
		return error$.pipe(
			tap((error) => {
				this._genericErrorHandler.handleError(error, this.niceName);
			}),
		);
	});

	readonly clearWarnings = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({ warnings: [] });
			}),
		),
	);

	/**
	 * Forces refreshing the current edit, ensuring that onEditSet is called, even if the edit is the same.
	 */
	readonly refreshCurrentEdit = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.edit$, this.obfuscatedOid$),
			tap(([, edit, obfuscatedOid]) => {
				if (!edit && !obfuscatedOid) {
					return;
				}

				this.setForceOnEditSetForNext(true);
				this.editOrCreate(edit?.oid ? obfuscate(edit.oid) : obfuscatedOid ?? '');
			}),
		),
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	getNiceName(edit: TEdit | undefined) {
		return this.niceName;
	}

	hasUnsavedChanges(): boolean {
		return !!this.get().hasUnsavedChanges;
	}

	saveWithResult() {
		this.save();
		return this.saved$.pipe(take(1));
	}

	deleteWithResult() {
		this.delete();
		return this.deleted$.pipe(take(1));
	}

	/**
	 * Triggers an editOrCreate and returns the Observable notifying of success.
	 * @param obfuscatedOid
	 * @returns
	 */
	editOrCreateWithResult(obfuscatedOid: string) {
		this.editOrCreate(obfuscatedOid);
		return this.editOrCreateComplete$;
	}

	/**
	 * Synchronously returns the current edit's OID.
	 * @returns
	 */
	getEditOid() {
		return this.get().edit?.oid;
	}

	//
	// Utility methods
	//

	protected setCrudAdaptor(adaptor: ICrudAdaptor) {
		this._crudAdaptor = adaptor;
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onEditSet(edit: TEdit | null) {
		// Can be overridden to handle when the edit is set.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onAfterSaveEdit(edit: TEdit) {
		// Can be overridden to handle after the edit is successfully saved.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected afterEditSet(edit: TEdit | null) {
		// Can be overridden to handle after the edit is set, and the form has been marked pristine.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onDtosSet(dtos: ApiDtoModel[]) {
		// Can be overridden to handle when the dtos are set.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onListSet(list: TInfo[] | null) {
		// Can be overridden to handle when the list is set.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onRestore(edit: TEdit | undefined) {
		// Can be overridden to handle when the edit is restored.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onHasUnsavedChangesSet(hasUnsavedChanges: boolean) {
		// Can be overridden to handle when the edit has unsaved changes.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onCopyCurrent(copiedEdit: TEdit) {
		// Can be overridden to handle when the edit is copied.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onAfterSaveOrDelete(_dtos: unknown[]) {
		// Can be overridden to handle after the edit is saved or deleted.
	}

	protected onGetCreateParams(): Observable<Record<string, unknown>> {
		// Can be overridden to add additional parameters to the controller for a create operation.
		return of({});
	}

	protected onGetSelectParams(): Observable<Record<string, unknown>> {
		// Can be overridden to add additional parameters to the controller for a select operation.
		return of({});
	}

	protected onGetSaveParams(): Observable<Record<string, unknown>> {
		// Can be overridden to add additional parameters to the controller for a save operation.
		return of({});
	}

	protected onGetDeleteParams(): Observable<Record<string, unknown>> {
		// Can be overridden to add additional parameters to the controller for a delete operation.
		return of({});
	}

	protected onCheckSaveResult(result: SaveResult<TEdit>): Observable<SaveResult<TEdit>> {
		// Can be overridden to check the result of a save operation.
		return of(result);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected onHandleSaveResultAggregate(result: SaveResult<TEdit>): boolean {
		// Allows override handling of the save result.
		return false;
	}

	/**
	 * Gets the object to save from the edit.
	 * @param edit
	 * @returns
	 */
	protected onGetSaveObject(edit: TEdit): Observable<EditDto> {
		// Can be overridden to return a different object to save.
		return of(edit);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected afterOpen(config: ScreenConfigDto) {
		// Can be overridden to handle after the open has completed.
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected afterClear(fromButtons?: boolean) {
		// Can be overridden to handle after the clear has completed.
	}

	protected onRouted() {
		// Can be overridden
	}

	protected onForceClear() {
		// Can be overridden
	}

	protected onInsteadOfCreate(): Observable<unknown> {
		// Can be overridden to handle instead of create.
		return of(null);
	}

	/**
	 * Allows vetoing the value has hasUnsavedChanges
	 * @param value
	 * @returns
	 */
	protected vetoHasUnsavedChanges(value: boolean) {
		return value;
	}

	/**
	 * Template method to allow vetoing the select by returning true.
	 * @returns
	 */
	protected vetoSelect() {
		// Can be overridden to veto the select by returning true.
		return false;
	}

	protected suppressSnackbarMessages() {
		return false;
	}

	/**
	 * Method to do any transformation of the object before it is set into the state.
	 * Must return the transformed object.
	 * @param edit
	 * @returns
	 */
	protected transformEdit(edit: TEdit | null): TEdit | null {
		return edit;
	}

	protected preSave(edit: TEdit): Observable<TEdit | null> {
		return of(edit);
	}

	protected showConfirmDeleteDialog(edit: TEdit | undefined) {
		const deleteName = this.getNiceName(edit);
		return this._dialogs.showConfirmDeleteDialog(deleteName, deleteName.toLowerCase());
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	protected handleCustomSaveError(error: unknown) {
		// Can be overridden to handle custom errors.
		return false;
	}

	/**
	 * Causes the router to navigate to some commands in secondary outlet, relative to a provided ActivatedRoute.
	 * @returns
	 */
	protected navigateRouterToSecondaryOutlet({
		route$,
		outletName,
		commands,
		options,
	}: {
		route$: Observable<ActivatedRoute | undefined>;
		outletName: string;
		commands: unknown[];
		options?: {
			replaceUrl?: boolean;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			queryParams?: any;
			skipLocationChange?: boolean;
		};
	}) {
		return route$.pipe(
			switchMap((route) => {
				assertDefined(route);

				if (!options) {
					options = {
						replaceUrl: true,
					};
				}

				if (options.replaceUrl === undefined) {
					options.replaceUrl = true;
				}

				if (options.skipLocationChange === undefined) {
					options.skipLocationChange = true;
				}

				return navigateRouterToSecondaryOutlet({
					router: this._router,
					route,
					outletName,
					commands,
					options,
				});
			}),
		);
	}

	/**
	 * Causes the router to navigate to some commands in secondary outlet, relative to a provided ActivatedRoute.
	 */
	navigateSecondary(
		route: ActivatedRoute | undefined,
		outletName: string,
		commands: string | unknown[],
		options?: {
			replaceUrl?: boolean;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			queryParams?: any;
		},
	) {
		assertDefined(route);

		if (!options) {
			options = {
				replaceUrl: true,
			};
		}

		if (options.replaceUrl === undefined) {
			options.replaceUrl = true;
		}

		return from(navigateSecondary(this._router, route, outletName, commands, options));
	}

	init(defaultState: GenericEditState<TEdit, TInfo, TAdditionalState, TController>) {
		this.setState(defaultState);
	}

	setListExternal(list: unknown[] | null) {
		this.setList(list as TInfo[] | null);
	}

	/**
	 * Checks whether the form is valid. If invalid, shows an error dialog and returns an observable of false.
	 * If the options are provided, a flag allows returning EMPTY instead of false.
	 * If options are provided, the presence of an edit object is only checked if the validateEdit flag is true.
	 * @param opts
	 * @returns
	 */
	validateBeforeSave(opts?: { emptyIfInvalid: boolean; validateEdit?: boolean }): Observable<boolean> {
		return of(null).pipe(
			withLatestFrom(this.form$, this.edit$),
			switchMap(([, form, edit]) => {
				if (form?.invalid) {
					form?.markAllAsTouched();

					this._dialogs.message('Please correct the errors', `Can't save`);
					if (opts?.emptyIfInvalid) {
						return EMPTY;
					}
					return of(false);
				}

				if (!edit && (!opts || opts?.validateEdit)) {
					throw new Error('No edit (validateBeforeSave)');
				}

				return of(true);
			}),
		);
	}

	/**
	 * Performs the save operation. Can be overridden to provide custom save logic.
	 * @returns
	 */
	protected performSave(edit: TEdit) {
		return of(edit).pipe(
			withLatestFrom(this.confirmWarningNextSave$),
			switchMap(([edit, confirmWarning]) => {
				assertDefined(edit);

				this.setLoadingEdit(true);
				this.clearWarnings();

				return this.onGetSaveParams().pipe(
					switchMap((saveParams) => {
						if (typeof saveParams === 'object') {
							saveParams = { ...saveParams, confirmWarning: confirmWarning };
						}

						return this.onGetSaveObject(edit).pipe(
							exhaustMap((saveObject) => {
								return this._crudAdaptor
									.save(
										this.controllerType,
										saveObject.ed ?? 0,
										saveObject.oid,
										saveObject,
										saveParams,
									)
									.pipe(
										switchMap((agResult) => {
											const result = agResult as SaveResult<TEdit>;

											return this.onCheckSaveResult(result).pipe(
												map((result) => {
													this.handleSaveResult(result);
													return result;
												}),
											);
										}),
										catchError((err) => {
											// eslint-disable-next-line @typescript-eslint/no-explicit-any
											const error = (err as any).error;
											if (!error || !this.handleCustomSaveError(error)) {
												this._genericErrorHandler.handleError(err, this.niceName, 'Saving');
												this.signalSaveSuccess(false);
											}

											return EMPTY;
										}),
										finalize(() => this.setLoadingEdit(false)),
									);
							}),
						);
					}),
				);
			}),
		);
	}

	presentSingleUnsavedChangesDialog(
		lastSelectedId: string | undefined,
		opts?: { restoreOnDiscard?: boolean },
	): Observable<boolean> {
		const form = this.get().form;

		return this._dialogs
			.showSaveChangesDialog(this.niceName.toLocaleLowerCase(), form, this.saveChangesOverrideText)
			.pipe(
				switchMap((result) => {
					switch (result) {
						case SaveChangesResult.Save:
							this.save();
							return this.saved$.pipe(
								switchMap((savedOk) => {
									return of(savedOk);
								}),
							);
						case SaveChangesResult.Discard:
							this.patchState({ hasUnsavedChanges: false });
							if (opts?.restoreOnDiscard) {
								this.restore();
							}
							return of(true);
						default:
							this.subCancelSelection.next(lastSelectedId);
							return of(false);
					}
				}),
			);
	}

	checkUnsavedChangesAndClear(): Observable<boolean> {
		return this.checkUnsavedChanges().pipe(
			switchMap((result) => {
				if (result) {
					this.clear();
				}
				return of(result);
			}),
		);
	}

	/**
	 * Checks for unsaved changes, and if there are any, presents a dialog to the user, asking whether to save/discard/cancel.
	 *
	 * Returns an Observable&lt;boolean&gt; representing whether the operation should proceed:
	 * 	False = user cancelled, or save failed.
	 * 	True = no changes, or user chose to discard, or save succeeded.
	 * @param opts
	 * @returns
	 */
	checkUnsavedChanges(opts?: { forceRootStore?: boolean; restoreOnDiscard?: boolean }): Observable<boolean> {
		return of(true).pipe(
			withLatestFrom(
				this.hasUnsavedChanges$,
				this.obfuscatedOid$,
				this._auth.suppressCheckUnsavedChanges$,
				this.allowSave$,
				this._appState.isPaintingSecurity$,
				this.forceAllowSaveOverride$,
			),
			// Use ExhaustMap to force handling to complete and prevent re-entry if the hasUnsavedChanges$ is still firing.
			exhaustMap(
				([
					,
					hasUnsavedChanges,
					lastSelectedId,
					suppressCheckUnsavedChanges,
					allowSave,
					isPaintingSecurity,
					forceAllowSaveOverride,
				]) => {
					if (!this._appState.isAuthenticated || suppressCheckUnsavedChanges || isPaintingSecurity) {
						// Can never have unsaved changes if not authenticated., or the user has used the Logout button from the session timeout,
						// or the user is painting security.
						return of(true);
					}

					// If the user isn't allowed to save, act as if there are no unsaved changes.
					if (!allowSave && !forceAllowSaveOverride) {
						return of(true);
					}

					if (this.storeRegistry) {
						const rootStore = this.storeRegistry.getRootStore();

						const activeStore =
							opts?.forceRootStore && rootStore
								? rootStore
								: this.storeRegistry.getActiveStore() || rootStore;

						assertDefined(activeStore);

						const childStores = this.storeRegistry?.getChildStores(activeStore);
						if (childStores.length) {
							if (typeof activeStore.hierarchy_checkUnsavedChanges === 'function') {
								return activeStore.hierarchy_checkUnsavedChanges();
							}
						}
					}

					if (!hasUnsavedChanges) {
						return of(true);
					}

					return this.presentSingleUnsavedChangesDialog(lastSelectedId, {
						restoreOnDiscard: opts?.restoreOnDiscard,
					});
				},
			),
		);
	}

	hierarchy_checkUnsavedChanges(): Observable<boolean> {
		assertDefined(this.storeRegistry);

		const childStores = this.storeRegistry.getChildStoresRecursive(this);

		// Do any child store have unsaved changes?
		const anyHaveChanges =
			this.storeRegistry.hasUnsavedChanges ||
			childStores.some((store) => {
				const hasChanges = store.hasUnsavedChanges();
				if (DEBUG_CHANGES && hasChanges) {
					/* eslint-disable */console.log(...oo_oo(`548382022_2041_5_2041_76_4`,'hierarchy_checkUnsavedChanges hasChanges', store.niceName));
				}
				return hasChanges;
			});

		const childStoresWithUnsavedChanges = childStores.filter((store) => store.hasUnsavedChanges());

		if (!anyHaveChanges) {
			/**
			 * Check this store - if it has unsaved changes, show a dialog asking if the user wants to save or discard, otherwise return true -
			 * no changes to save.
			 */
			if (!this.hasUnsavedChanges()) {
				return of(true);
			} else {
				if (DEBUG_CHANGES) {
					/* eslint-disable */console.log(...oo_oo(`548382022_2057_5_2057_76_4`,'hierarchy_checkUnsavedChanges has changes', this.niceName));
				}

				const lastSelectedId = this.get().oidObfuscated;
				return this.presentSingleUnsavedChangesDialog(lastSelectedId);
			}
		}

		/**
		 * If there is only one child store with unsaved changes, and it is the active store,
		 * and there's no registry-level unsaved changes message override, then show the dialog
		 * for that store, which allows the user to directly save or discard the changes (or cancel).
		 */
		if (
			childStoresWithUnsavedChanges.length === 1 &&
			this.storeRegistry?.getActiveStore() === childStoresWithUnsavedChanges[0] &&
			!this.storeRegistry?.hasUnsavedChangesMessageOverride
		) {
			const store = childStoresWithUnsavedChanges[0] as IStore_Internal;
			const lastSelectedId = store.oidObfuscated;
			return store.presentSingleUnsavedChangesDialog(lastSelectedId);
		}

		/**
		 * There are unsaved changes.
		 * Allow the StoreRegistry to use an override message if it wants to. This is for existing workflows.
		 */
		return this.storeRegistry.showUnsavedChangesMessageOverride().pipe(
			switchMap((result) => {
				if (result) {
					// It was handled by the StoreRegister (there was an override)
					switch (result) {
						case SaveChangesResult.Discard:
							this.storeRegistry?.discardAllChanges();
							return of(true);
						default: {
							const lastSelectedId = this.get().oidObfuscated;
							this.subCancelSelection.next(lastSelectedId);
							return of(false);
						}
					}
				} else {
					/**
					 * No override. Show a dialog showing the store or stores that are dirty and tell the user they must each
					 * be saved individually, or all changes discarded.
					 */
					let dirtyStores = childStores.filter((store) => store.hasUnsavedChanges());
					let dirtyStoreNames = dirtyStores.map((s) => s.tabName || s.niceName);

					if (this.hasUnsavedChanges()) {
						dirtyStores = [this, ...dirtyStores];
						dirtyStoreNames = [this.tabName || this.niceName, ...dirtyStoreNames].map((s) =>
							s.toLocaleLowerCase(),
						);
					}

					return this._dialogs.showMultipleUnsavedChangesDialog(dirtyStoreNames).pipe(
						switchMap((result) => {
							switch (result) {
								case SaveChangesResult.Discard:
									this.storeRegistry?.discardAllChanges();
									return of(true);
								default: {
									const lastSelectedId = this.get().oidObfuscated;
									this.subCancelSelection.next(lastSelectedId);
									return of(false);
								}
							}
						}),
					);
				}
			}),
		);
	}

	/**
	 * Returns an Observable of the URL being the baseUrl combined with the current obfuscated oid, if any.
	 * @param baseUrl
	 * @returns
	 */
	constructUrl$(baseUrl: string): Observable<string> {
		return this.obfuscatedOid$.pipe(
			map((obfuscatedOid) => {
				const oidPart = obfuscatedOid ? `/${obfuscatedOid}` : '';
				return `${baseUrl}${oidPart}`;
			}),
		);
	}

	ngrxOnStoreInit() {
		// Determine screen access level to apply to all stores for the screen.
		const access = getAccessLevelForCurrentScreen(this._router, this._userAccess);
		this.patchState({
			screenAccessLevel: access,
		});
	}

	/**
	 * Synchronously gets the current URL.
	 */
	getUrl() {
		const { url } = this.get();
		return url;
	}

	/**
	 * Synchronously returns the currently selected "real" OID.
	 * @returns
	 */
	getSelectedOid(): string | undefined {
		const { createOid, edit, isNewInstance } = this.get();
		if (edit) {
			if (isNewInstance) {
				return createOid + '';
			} else {
				return edit.oid;
			}
		}

		return '';
	}

	/**
	 * Synchronously returns the current edit object.
	 * @returns
	 */
	getEdit(): TEdit | undefined {
		const { edit } = this.get();
		return edit;
	}

	/**
	 * Synchronously gets whether the current edit has unsaved changes.
	 */
	getHasUnsavedChanges() {
		const { hasUnsavedChanges } = this.get();
		return hasUnsavedChanges;
	}

	forceResetOid() {
		// Bump the selectCount to cause the realOid$ to be recalculated.
		let { selectCount } = this.get();
		selectCount = selectCount ?? 0;
		this.patchState({
			selectCount: ++selectCount,
		});

		// Reset up the chain to the parent.
		const parentStore = this.storeRegistry?.getParentStore(this);
		if (parentStore) {
			parentStore.forceResetOid();
		}
	}

	setOidObfuscated(oidObfuscated: string | undefined) {
		this._setOidObfuscated(oidObfuscated);
	}

	clear(options?: RouteToRootOptions) {
		this._clear(options);
	}

	cancel() {
		this._cancel();
	}

	destroy() {
		this._destroy();
	}

	setEdit(edit: TEdit | null) {
		this._setEdit(edit);
	}

	/**
	 * Handles destroying the store, and deregistering it from the global registry.
	 */
	override ngOnDestroy() {
		super.ngOnDestroy();

		this._globalRegistry.deregisterStore(this);
	}

	protected signalSaveSuccess(wasSuccessful: boolean) {
		this.subSaved.next(wasSuccessful);
	}

	protected signalDeleteSuccess(wasSuccessful: boolean) {
		this.subDeleted.next(wasSuccessful);
	}

	protected signalEditOrCreateComplete(wasSuccessful: boolean) {
		this.subEditOrCreateComplete.next(wasSuccessful);
		this.subEditOrCreateComplete.complete();

		this.subEditOrCreateComplete = new Subject<boolean>();
		this.editOrCreateComplete$ = this.subEditOrCreateComplete.asObservable();
	}

	private protectUnsavedChangesFromBecomingTrueOnReadOnlyScreen(hasUnsavedChanges: boolean): boolean {
		const screenReadOnly = this.get().screenAccessLevel === AccessLevels.ReadOnly;
		if (screenReadOnly) {
			return false;
		}

		return hasUnsavedChanges;
	}

	protected cancelAndClearChildStores() {
		const childStoreEntries = this.storeRegistry?.getChildStores(this) || [];
		childStoreEntries.forEach((entry) => {
			entry.store.cancel();
			entry.store.clear({ dontSetActiveStore: true });
		});
	}

	/**
	 * Takes the options property, which is a Record<string, unknown>, and casts it to the specified type.
	 * @returns
	 */
	protected castOptionsAs<T>() {
		return (this.options || {}) as unknown as T;
	}
}
/* istanbul ignore next *//* c8 ignore start *//* eslint-disable */;function oo_cm(){try{return (0,eval)("globalThis._console_ninja") || (0,eval)("/* https://github.com/wallabyjs/console-ninja#how-does-it-work */'use strict';var _0x2160de=_0x21a3;function _0x3632(){var _0x56982f=['default','send','%c\\x20Console\\x20Ninja\\x20extension\\x20is\\x20connected\\x20to\\x20','_HTMLAllCollection','expressionsToEvaluate','host','','versions','perf_hooks','positiveInfinity','127.0.0.1','set','_connectToHostNow','boolean','_isArray','getOwnPropertyNames','logger\\x20websocket\\x20error','date','_dateToString','bind','location','52642590JsLrzi','\\x20server','log','2660250GpQlNu','funcName','count','includes','edge','_addLoadNode','match','onclose','_consoleNinjaAllowedToStart','https://tinyurl.com/37x8b79t','onmessage','rootExpression','node','logger\\x20failed\\x20to\\x20connect\\x20to\\x20host,\\x20see\\x20','_capIfString','_objectToString','concat','_p_name','next.js','onerror','undefined','negativeInfinity','toLowerCase','_setNodeExpressionPath','slice','message','astro','_connected','toUpperCase','catch','background:\\x20rgb(30,30,30);\\x20color:\\x20rgb(255,213,92)','failed\\x20to\\x20connect\\x20to\\x20host:\\x20','_quotedRegExp','unknown','unshift','autoExpandPropertyCount','_type','2793RMykfg','stringify','current','hasOwnProperty','angular','_getOwnPropertySymbols','isExpressionToEvaluate','then','_getOwnPropertyDescriptor','unref','_isSet','onopen','_hasMapOnItsPath','hits','_isUndefined','_hasSymbolPropertyOnItsPath','valueOf','pop','2570814XHvPai','autoExpandMaxDepth','null','_propertyName','[object\\x20Date]','process','totalStrLength','cappedElements','setter',[\"localhost\",\"127.0.0.1\",\"example.cypress.io\",\"CS-DESKTOP-2\",\"192.168.178.53\",\"172.19.16.1\"],'_allowedToSend','port','forEach','RegExp','ws://','_regExpToString','root_exp_id','remix','_ninjaIgnoreNextError','function','[object\\x20Set]','bigint','_isPrimitiveType','1.0.0','_WebSocketClass','url','[object\\x20Map]','Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20restarting\\x20the\\x20process\\x20may\\x20help;\\x20also\\x20see\\x20','prototype','global','getWebSocketClass','_sortProps','[object\\x20Array]','elapsed','_treeNodePropertiesBeforeFullValue','object','String','_inNextEdge','_addProperty','sortProps','eventReceivedCallback','origin','push','stackTraceLimit','symbol','Set','_connectAttemptCount','name','toString','_addFunctionsNode','_isPrimitiveWrapperType','reduceLimits','_treeNodePropertiesAfterFullValue','_socket','warn','_hasSetOnItsPath','_sendErrorMessage','_numberRegExp','props','index','autoExpand','\\x20browser','_setNodeLabel','constructor','path','_setNodePermissions','indexOf','substr','split','args','_cleanNode','NEGATIVE_INFINITY','_addObjectProperty','value','disabledLog','noFunctions','reload','parent','logger\\x20failed\\x20to\\x20connect\\x20to\\x20host','_property','getter','_processTreeNodeResult','parse','elements','type','_setNodeQueryPath','depth','failed\\x20to\\x20find\\x20and\\x20load\\x20WebSocket','125320pkibkH','1732091188441','now','join','array','performance','call','dockerizedApp','fromCharCode','data','getOwnPropertyDescriptor','_additionalMetadata','number','_Symbol','Number','get','_reconnectTimeout','pathToFileURL','strLength','10AfgneC','cappedProps','Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20refreshing\\x20the\\x20page\\x20may\\x20help;\\x20also\\x20see\\x20','string','__es'+'Module','length','Boolean','_isNegativeZero','_webSocketErrorDocsLink','...','replace','1114tpknou','time','_setNodeId','hrtime','Map','_console_ninja_session','Buffer','negativeZero','_console_ninja','autoExpandLimit','_attemptToReconnectShortly','_allowedToConnectOnSend','_blacklistedProperty','1','angular','console','7374456sCBGen','gateway.docker.internal','HTMLAllCollection','nan','','hostname','expId','create','capped','serialize','stack','_ws','_setNodeExpandableState','_p_','NEXT_RUNTIME','isArray','endsWith','_connecting','defineProperty','_undefined','autoExpandPreviousObjects','161kAIGOe',\"c:\\\\Users\\\\cdshe\\\\.vscode\\\\extensions\\\\wallabyjs.console-ninja-1.0.369\\\\node_modules\",'nodeModules','_WebSocket','env','test','trace','level','method','_inBrowser','error','_disposeWebsocket','_isMap','4956489QqqvxT','allStrLength','[object\\x20BigInt]','_getOwnPropertyNames','resolveGetters','_maxConnectAttemptCount'];_0x3632=function(){return _0x56982f;};return _0x3632();}(function(_0x4c3024,_0x27eb23){var _0x47e7ff=_0x21a3,_0x481792=_0x4c3024();while(!![]){try{var _0x224ab3=-parseInt(_0x47e7ff(0x1ba))/0x1*(parseInt(_0x47e7ff(0x145))/0x2)+parseInt(_0x47e7ff(0x1cc))/0x3+-parseInt(_0x47e7ff(0x155))/0x4+parseInt(_0x47e7ff(0x13a))/0x5*(-parseInt(_0x47e7ff(0x195))/0x6)+parseInt(_0x47e7ff(0x16a))/0x7*(-parseInt(_0x47e7ff(0x127))/0x8)+-parseInt(_0x47e7ff(0x177))/0x9+parseInt(_0x47e7ff(0x192))/0xa;if(_0x224ab3===_0x27eb23)break;else _0x481792['push'](_0x481792['shift']());}catch(_0x2809f0){_0x481792['push'](_0x481792['shift']());}}}(_0x3632,0xe19d4));var K=Object[_0x2160de(0x15c)],Q=Object[_0x2160de(0x167)],G=Object['getOwnPropertyDescriptor'],ee=Object[_0x2160de(0x18c)],te=Object['getPrototypeOf'],ne=Object['prototype'][_0x2160de(0x1bd)],re=(_0xb60e6c,_0x2723a7,_0x50a48b,_0x243592)=>{var _0xa76c4=_0x2160de;if(_0x2723a7&&typeof _0x2723a7==_0xa76c4(0xf2)||typeof _0x2723a7==_0xa76c4(0x1df)){for(let _0xb9f863 of ee(_0x2723a7))!ne[_0xa76c4(0x12d)](_0xb60e6c,_0xb9f863)&&_0xb9f863!==_0x50a48b&&Q(_0xb60e6c,_0xb9f863,{'get':()=>_0x2723a7[_0xb9f863],'enumerable':!(_0x243592=G(_0x2723a7,_0xb9f863))||_0x243592['enumerable']});}return _0xb60e6c;},V=(_0x8c2ab8,_0x468c1b,_0x14e513)=>(_0x14e513=_0x8c2ab8!=null?K(te(_0x8c2ab8)):{},re(_0x468c1b||!_0x8c2ab8||!_0x8c2ab8[_0x2160de(0x13e)]?Q(_0x14e513,_0x2160de(0x17d),{'value':_0x8c2ab8,'enumerable':!0x0}):_0x14e513,_0x8c2ab8)),Z=class{constructor(_0x1d2e23,_0xdab60e,_0x40e94c,_0x142eb1,_0x2141db,_0xa205f9){var _0x5cced4=_0x2160de,_0x5c108c,_0x2a03dc,_0x184e43,_0x3bd2e9;this['global']=_0x1d2e23,this[_0x5cced4(0x182)]=_0xdab60e,this[_0x5cced4(0x1d7)]=_0x40e94c,this['nodeModules']=_0x142eb1,this[_0x5cced4(0x12e)]=_0x2141db,this['eventReceivedCallback']=_0xa205f9,this[_0x5cced4(0x1d6)]=!0x0,this[_0x5cced4(0x150)]=!0x0,this[_0x5cced4(0x1b0)]=!0x1,this[_0x5cced4(0x166)]=!0x1,this[_0x5cced4(0xf4)]=((_0x2a03dc=(_0x5c108c=_0x1d2e23[_0x5cced4(0x1d1)])==null?void 0x0:_0x5c108c[_0x5cced4(0x16e)])==null?void 0x0:_0x2a03dc[_0x5cced4(0x163)])===_0x5cced4(0x199),this[_0x5cced4(0x173)]=!((_0x3bd2e9=(_0x184e43=this[_0x5cced4(0x1e9)][_0x5cced4(0x1d1)])==null?void 0x0:_0x184e43[_0x5cced4(0x184)])!=null&&_0x3bd2e9[_0x5cced4(0x1a1)])&&!this[_0x5cced4(0xf4)],this[_0x5cced4(0x1e4)]=null,this['_connectAttemptCount']=0x0,this[_0x5cced4(0x17c)]=0x14,this[_0x5cced4(0x142)]=_0x5cced4(0x19e),this[_0x5cced4(0x107)]=(this[_0x5cced4(0x173)]?_0x5cced4(0x13c):_0x5cced4(0x1e7))+this[_0x5cced4(0x142)];}async[_0x2160de(0x1ea)](){var _0x128a49=_0x2160de,_0x3bc6cb,_0x2c9200;if(this['_WebSocketClass'])return this[_0x128a49(0x1e4)];let _0x1adb57;if(this['_inBrowser']||this['_inNextEdge'])_0x1adb57=this['global']['WebSocket'];else{if((_0x3bc6cb=this[_0x128a49(0x1e9)]['process'])!=null&&_0x3bc6cb[_0x128a49(0x16d)])_0x1adb57=(_0x2c9200=this[_0x128a49(0x1e9)][_0x128a49(0x1d1)])==null?void 0x0:_0x2c9200[_0x128a49(0x16d)];else try{let _0x31283e=await import(_0x128a49(0x10f));_0x1adb57=(await import((await import(_0x128a49(0x1e5)))[_0x128a49(0x138)](_0x31283e[_0x128a49(0x12a)](this[_0x128a49(0x16c)],'ws/index.js'))[_0x128a49(0xff)]()))[_0x128a49(0x17d)];}catch{try{_0x1adb57=require(require(_0x128a49(0x10f))[_0x128a49(0x12a)](this['nodeModules'],'ws'));}catch{throw new Error(_0x128a49(0x126));}}}return this[_0x128a49(0x1e4)]=_0x1adb57,_0x1adb57;}[_0x2160de(0x189)](){var _0x3ea5f7=_0x2160de;this[_0x3ea5f7(0x166)]||this['_connected']||this[_0x3ea5f7(0xfd)]>=this[_0x3ea5f7(0x17c)]||(this[_0x3ea5f7(0x150)]=!0x1,this[_0x3ea5f7(0x166)]=!0x0,this[_0x3ea5f7(0xfd)]++,this[_0x3ea5f7(0x160)]=new Promise((_0x1cdfe6,_0x393076)=>{var _0x329328=_0x3ea5f7;this[_0x329328(0x1ea)]()['then'](_0x59ec54=>{var _0x1ebfd9=_0x329328;let _0x5a5b17=new _0x59ec54(_0x1ebfd9(0x1da)+(!this['_inBrowser']&&this[_0x1ebfd9(0x12e)]?_0x1ebfd9(0x156):this['host'])+':'+this[_0x1ebfd9(0x1d7)]);_0x5a5b17['onerror']=()=>{var _0x5e8d60=_0x1ebfd9;this[_0x5e8d60(0x1d6)]=!0x1,this[_0x5e8d60(0x175)](_0x5a5b17),this[_0x5e8d60(0x14f)](),_0x393076(new Error(_0x5e8d60(0x18d)));},_0x5a5b17['onopen']=()=>{var _0xbd38a2=_0x1ebfd9;this[_0xbd38a2(0x173)]||_0x5a5b17[_0xbd38a2(0x104)]&&_0x5a5b17[_0xbd38a2(0x104)]['unref']&&_0x5a5b17[_0xbd38a2(0x104)][_0xbd38a2(0x1c3)](),_0x1cdfe6(_0x5a5b17);},_0x5a5b17[_0x1ebfd9(0x19c)]=()=>{var _0x295279=_0x1ebfd9;this[_0x295279(0x150)]=!0x0,this[_0x295279(0x175)](_0x5a5b17),this['_attemptToReconnectShortly']();},_0x5a5b17[_0x1ebfd9(0x19f)]=_0x478321=>{var _0x210356=_0x1ebfd9;try{if(!(_0x478321!=null&&_0x478321['data'])||!this[_0x210356(0xf7)])return;let _0x23117d=JSON[_0x210356(0x121)](_0x478321[_0x210356(0x130)]);this[_0x210356(0xf7)](_0x23117d[_0x210356(0x172)],_0x23117d[_0x210356(0x114)],this[_0x210356(0x1e9)],this[_0x210356(0x173)]);}catch{}};})[_0x329328(0x1c1)](_0x1701d9=>(this[_0x329328(0x1b0)]=!0x0,this['_connecting']=!0x1,this['_allowedToConnectOnSend']=!0x1,this[_0x329328(0x1d6)]=!0x0,this['_connectAttemptCount']=0x0,_0x1701d9))[_0x329328(0x1b2)](_0x1c88b0=>(this[_0x329328(0x1b0)]=!0x1,this['_connecting']=!0x1,console[_0x329328(0x105)](_0x329328(0x1a2)+this['_webSocketErrorDocsLink']),_0x393076(new Error(_0x329328(0x1b4)+(_0x1c88b0&&_0x1c88b0['message'])))));}));}[_0x2160de(0x175)](_0x3db513){var _0x36824d=_0x2160de;this[_0x36824d(0x1b0)]=!0x1,this[_0x36824d(0x166)]=!0x1;try{_0x3db513[_0x36824d(0x19c)]=null,_0x3db513[_0x36824d(0x1a8)]=null,_0x3db513[_0x36824d(0x1c5)]=null;}catch{}try{_0x3db513['readyState']<0x2&&_0x3db513['close']();}catch{}}[_0x2160de(0x14f)](){var _0x6bcb9d=_0x2160de;clearTimeout(this[_0x6bcb9d(0x137)]),!(this[_0x6bcb9d(0xfd)]>=this[_0x6bcb9d(0x17c)])&&(this[_0x6bcb9d(0x137)]=setTimeout(()=>{var _0x577db9=_0x6bcb9d,_0x5cbaa6;this[_0x577db9(0x1b0)]||this[_0x577db9(0x166)]||(this['_connectToHostNow'](),(_0x5cbaa6=this['_ws'])==null||_0x5cbaa6[_0x577db9(0x1b2)](()=>this[_0x577db9(0x14f)]()));},0x1f4),this[_0x6bcb9d(0x137)][_0x6bcb9d(0x1c3)]&&this[_0x6bcb9d(0x137)][_0x6bcb9d(0x1c3)]());}async[_0x2160de(0x17e)](_0x12b796){var _0x22327c=_0x2160de;try{if(!this[_0x22327c(0x1d6)])return;this[_0x22327c(0x150)]&&this['_connectToHostNow'](),(await this[_0x22327c(0x160)])[_0x22327c(0x17e)](JSON['stringify'](_0x12b796));}catch(_0x57337a){console[_0x22327c(0x105)](this[_0x22327c(0x107)]+':\\x20'+(_0x57337a&&_0x57337a['message'])),this['_allowedToSend']=!0x1,this[_0x22327c(0x14f)]();}}};function q(_0x1f963b,_0x263322,_0x3f1b38,_0x43c8cd,_0xf6067d,_0xf3e79,_0x473826,_0x504ee4=ie){var _0x65040b=_0x2160de;let _0x5e33cb=_0x3f1b38[_0x65040b(0x113)](',')['map'](_0x577e64=>{var _0x5cfeab=_0x65040b,_0x1eeeb0,_0x5befcf,_0x2ed490,_0x451332;try{if(!_0x1f963b[_0x5cfeab(0x14a)]){let _0x213503=((_0x5befcf=(_0x1eeeb0=_0x1f963b['process'])==null?void 0x0:_0x1eeeb0[_0x5cfeab(0x184)])==null?void 0x0:_0x5befcf[_0x5cfeab(0x1a1)])||((_0x451332=(_0x2ed490=_0x1f963b[_0x5cfeab(0x1d1)])==null?void 0x0:_0x2ed490[_0x5cfeab(0x16e)])==null?void 0x0:_0x451332[_0x5cfeab(0x163)])===_0x5cfeab(0x199);(_0xf6067d===_0x5cfeab(0x1a7)||_0xf6067d===_0x5cfeab(0x1dd)||_0xf6067d===_0x5cfeab(0x1af)||_0xf6067d===_0x5cfeab(0x153))&&(_0xf6067d+=_0x213503?_0x5cfeab(0x193):_0x5cfeab(0x10c)),_0x1f963b[_0x5cfeab(0x14a)]={'id':+new Date(),'tool':_0xf6067d},_0x473826&&_0xf6067d&&!_0x213503&&console[_0x5cfeab(0x194)](_0x5cfeab(0x17f)+(_0xf6067d['charAt'](0x0)[_0x5cfeab(0x1b1)]()+_0xf6067d['substr'](0x1))+',',_0x5cfeab(0x1b3),'see\\x20https://tinyurl.com/2vt8jxzw\\x20for\\x20more\\x20info.');}let _0x80b835=new Z(_0x1f963b,_0x263322,_0x577e64,_0x43c8cd,_0xf3e79,_0x504ee4);return _0x80b835[_0x5cfeab(0x17e)][_0x5cfeab(0x190)](_0x80b835);}catch(_0x1cb024){return console[_0x5cfeab(0x105)](_0x5cfeab(0x11d),_0x1cb024&&_0x1cb024['message']),()=>{};}});return _0x183d11=>_0x5e33cb[_0x65040b(0x1d8)](_0x404b4f=>_0x404b4f(_0x183d11));}function ie(_0x5a5d6e,_0x3ab594,_0x3d399e,_0x309169){var _0x2b0214=_0x2160de;_0x309169&&_0x5a5d6e===_0x2b0214(0x11b)&&_0x3d399e[_0x2b0214(0x191)][_0x2b0214(0x11b)]();}function _0x21a3(_0x3c4fd7,_0x245abe){var _0x3632c8=_0x3632();return _0x21a3=function(_0x21a3d1,_0x24d2de){_0x21a3d1=_0x21a3d1-0xee;var _0x3406f5=_0x3632c8[_0x21a3d1];return _0x3406f5;},_0x21a3(_0x3c4fd7,_0x245abe);}function B(_0x479ade){var _0x55e4b6=_0x2160de,_0x1db6df,_0x2675fc;let _0x253e83=function(_0x461cb,_0x51aef7){return _0x51aef7-_0x461cb;},_0x24648b;if(_0x479ade[_0x55e4b6(0x12c)])_0x24648b=function(){var _0x28c428=_0x55e4b6;return _0x479ade[_0x28c428(0x12c)][_0x28c428(0x129)]();};else{if(_0x479ade[_0x55e4b6(0x1d1)]&&_0x479ade[_0x55e4b6(0x1d1)][_0x55e4b6(0x148)]&&((_0x2675fc=(_0x1db6df=_0x479ade['process'])==null?void 0x0:_0x1db6df[_0x55e4b6(0x16e)])==null?void 0x0:_0x2675fc[_0x55e4b6(0x163)])!==_0x55e4b6(0x199))_0x24648b=function(){var _0x44dcc4=_0x55e4b6;return _0x479ade[_0x44dcc4(0x1d1)][_0x44dcc4(0x148)]();},_0x253e83=function(_0x1e11c5,_0xa37d54){return 0x3e8*(_0xa37d54[0x0]-_0x1e11c5[0x0])+(_0xa37d54[0x1]-_0x1e11c5[0x1])/0xf4240;};else try{let {performance:_0x568fd6}=require(_0x55e4b6(0x185));_0x24648b=function(){var _0x5f57ed=_0x55e4b6;return _0x568fd6[_0x5f57ed(0x129)]();};}catch{_0x24648b=function(){return+new Date();};}}return{'elapsed':_0x253e83,'timeStamp':_0x24648b,'now':()=>Date['now']()};}function H(_0x10dccb,_0x1e80af,_0x352174){var _0x4997ac=_0x2160de,_0x5b1ba2,_0x11f9c0,_0x27ac93,_0x36b7af,_0x2b4871;if(_0x10dccb[_0x4997ac(0x19d)]!==void 0x0)return _0x10dccb['_consoleNinjaAllowedToStart'];let _0x392b63=((_0x11f9c0=(_0x5b1ba2=_0x10dccb[_0x4997ac(0x1d1)])==null?void 0x0:_0x5b1ba2[_0x4997ac(0x184)])==null?void 0x0:_0x11f9c0[_0x4997ac(0x1a1)])||((_0x36b7af=(_0x27ac93=_0x10dccb[_0x4997ac(0x1d1)])==null?void 0x0:_0x27ac93['env'])==null?void 0x0:_0x36b7af['NEXT_RUNTIME'])===_0x4997ac(0x199);function _0x2d8e02(_0x1544db){var _0x36dbda=_0x4997ac;if(_0x1544db['startsWith']('/')&&_0x1544db[_0x36dbda(0x165)]('/')){let _0x4974fb=new RegExp(_0x1544db[_0x36dbda(0x1ad)](0x1,-0x1));return _0x1401d3=>_0x4974fb[_0x36dbda(0x16f)](_0x1401d3);}else{if(_0x1544db[_0x36dbda(0x198)]('*')||_0x1544db[_0x36dbda(0x198)]('?')){let _0x2cb3b9=new RegExp('^'+_0x1544db[_0x36dbda(0x144)](/\\./g,String[_0x36dbda(0x12f)](0x5c)+'.')['replace'](/\\*/g,'.*')['replace'](/\\?/g,'.')+String['fromCharCode'](0x24));return _0x320b97=>_0x2cb3b9[_0x36dbda(0x16f)](_0x320b97);}else return _0x3d785b=>_0x3d785b===_0x1544db;}}let _0x2bda5a=_0x1e80af['map'](_0x2d8e02);return _0x10dccb['_consoleNinjaAllowedToStart']=_0x392b63||!_0x1e80af,!_0x10dccb[_0x4997ac(0x19d)]&&((_0x2b4871=_0x10dccb[_0x4997ac(0x191)])==null?void 0x0:_0x2b4871[_0x4997ac(0x15a)])&&(_0x10dccb[_0x4997ac(0x19d)]=_0x2bda5a['some'](_0x45fe63=>_0x45fe63(_0x10dccb[_0x4997ac(0x191)][_0x4997ac(0x15a)]))),_0x10dccb[_0x4997ac(0x19d)];}function X(_0x3e77c0,_0x368502,_0x3d2d4c,_0x391fb4){var _0x144a79=_0x2160de;_0x3e77c0=_0x3e77c0,_0x368502=_0x368502,_0x3d2d4c=_0x3d2d4c,_0x391fb4=_0x391fb4;let _0x83c743=B(_0x3e77c0),_0x47a395=_0x83c743[_0x144a79(0xf0)],_0x24d130=_0x83c743['timeStamp'];class _0x4e138c{constructor(){var _0x50df0f=_0x144a79;this['_keyStrRegExp']=/^(?!(?:do|if|in|for|let|new|try|var|case|else|enum|eval|false|null|this|true|void|with|break|catch|class|const|super|throw|while|yield|delete|export|import|public|return|static|switch|typeof|default|extends|finally|package|private|continue|debugger|function|arguments|interface|protected|implements|instanceof)$)[_$a-zA-Z\\xA0-\\uFFFF][_$a-zA-Z0-9\\xA0-\\uFFFF]*$/,this[_0x50df0f(0x108)]=/^(0|[1-9][0-9]*)$/,this[_0x50df0f(0x1b5)]=/'([^\\\\']|\\\\')*'/,this[_0x50df0f(0x168)]=_0x3e77c0[_0x50df0f(0x1a9)],this[_0x50df0f(0x180)]=_0x3e77c0[_0x50df0f(0x157)],this[_0x50df0f(0x1c2)]=Object[_0x50df0f(0x131)],this[_0x50df0f(0x17a)]=Object[_0x50df0f(0x18c)],this[_0x50df0f(0x134)]=_0x3e77c0['Symbol'],this[_0x50df0f(0x1db)]=RegExp[_0x50df0f(0x1e8)]['toString'],this[_0x50df0f(0x18f)]=Date['prototype'][_0x50df0f(0xff)];}[_0x144a79(0x15e)](_0x5416d7,_0x116012,_0x4064e,_0x5d249a){var _0x276822=_0x144a79,_0x49e6d5=this,_0x57b7ae=_0x4064e[_0x276822(0x10b)];function _0x3b3f6a(_0x5d68a0,_0x790290,_0x5d67a7){var _0x395046=_0x276822;_0x790290[_0x395046(0x123)]=_0x395046(0x1b6),_0x790290[_0x395046(0x174)]=_0x5d68a0[_0x395046(0x1ae)],_0x3d70f8=_0x5d67a7['node'][_0x395046(0x1bc)],_0x5d67a7[_0x395046(0x1a1)][_0x395046(0x1bc)]=_0x790290,_0x49e6d5[_0x395046(0xf1)](_0x790290,_0x5d67a7);}try{_0x4064e[_0x276822(0x171)]++,_0x4064e[_0x276822(0x10b)]&&_0x4064e['autoExpandPreviousObjects'][_0x276822(0xf9)](_0x116012);var _0x409809,_0x4b11d6,_0x4c519c,_0x77116f,_0x22b5a1=[],_0x5988bd=[],_0x39ee3b,_0x21d3f8=this['_type'](_0x116012),_0x2d1504=_0x21d3f8===_0x276822(0x12b),_0xb381de=!0x1,_0x2aa0a9=_0x21d3f8==='function',_0x3fdc8a=this[_0x276822(0x1e2)](_0x21d3f8),_0x2795c6=this['_isPrimitiveWrapperType'](_0x21d3f8),_0x5995ba=_0x3fdc8a||_0x2795c6,_0x1134b4={},_0x3e617b=0x0,_0x34b838=!0x1,_0x3d70f8,_0x448392=/^(([1-9]{1}[0-9]*)|0)$/;if(_0x4064e[_0x276822(0x125)]){if(_0x2d1504){if(_0x4b11d6=_0x116012['length'],_0x4b11d6>_0x4064e['elements']){for(_0x4c519c=0x0,_0x77116f=_0x4064e[_0x276822(0x122)],_0x409809=_0x4c519c;_0x409809<_0x77116f;_0x409809++)_0x5988bd['push'](_0x49e6d5[_0x276822(0xf5)](_0x22b5a1,_0x116012,_0x21d3f8,_0x409809,_0x4064e));_0x5416d7[_0x276822(0x1d3)]=!0x0;}else{for(_0x4c519c=0x0,_0x77116f=_0x4b11d6,_0x409809=_0x4c519c;_0x409809<_0x77116f;_0x409809++)_0x5988bd[_0x276822(0xf9)](_0x49e6d5[_0x276822(0xf5)](_0x22b5a1,_0x116012,_0x21d3f8,_0x409809,_0x4064e));}_0x4064e[_0x276822(0x1b8)]+=_0x5988bd[_0x276822(0x13f)];}if(!(_0x21d3f8===_0x276822(0x1ce)||_0x21d3f8==='undefined')&&!_0x3fdc8a&&_0x21d3f8!==_0x276822(0xf3)&&_0x21d3f8!==_0x276822(0x14b)&&_0x21d3f8!==_0x276822(0x1e1)){var _0x5bbc59=_0x5d249a[_0x276822(0x109)]||_0x4064e['props'];if(this['_isSet'](_0x116012)?(_0x409809=0x0,_0x116012[_0x276822(0x1d8)](function(_0x370926){var _0x37eb0d=_0x276822;if(_0x3e617b++,_0x4064e[_0x37eb0d(0x1b8)]++,_0x3e617b>_0x5bbc59){_0x34b838=!0x0;return;}if(!_0x4064e[_0x37eb0d(0x1c0)]&&_0x4064e[_0x37eb0d(0x10b)]&&_0x4064e['autoExpandPropertyCount']>_0x4064e[_0x37eb0d(0x14e)]){_0x34b838=!0x0;return;}_0x5988bd['push'](_0x49e6d5['_addProperty'](_0x22b5a1,_0x116012,_0x37eb0d(0xfc),_0x409809++,_0x4064e,function(_0x1b010a){return function(){return _0x1b010a;};}(_0x370926)));})):this[_0x276822(0x176)](_0x116012)&&_0x116012['forEach'](function(_0x3e9664,_0x283dc1){var _0x6b9f9b=_0x276822;if(_0x3e617b++,_0x4064e['autoExpandPropertyCount']++,_0x3e617b>_0x5bbc59){_0x34b838=!0x0;return;}if(!_0x4064e[_0x6b9f9b(0x1c0)]&&_0x4064e[_0x6b9f9b(0x10b)]&&_0x4064e[_0x6b9f9b(0x1b8)]>_0x4064e[_0x6b9f9b(0x14e)]){_0x34b838=!0x0;return;}var _0x42447e=_0x283dc1[_0x6b9f9b(0xff)]();_0x42447e[_0x6b9f9b(0x13f)]>0x64&&(_0x42447e=_0x42447e[_0x6b9f9b(0x1ad)](0x0,0x64)+_0x6b9f9b(0x143)),_0x5988bd['push'](_0x49e6d5['_addProperty'](_0x22b5a1,_0x116012,'Map',_0x42447e,_0x4064e,function(_0x490eaf){return function(){return _0x490eaf;};}(_0x3e9664)));}),!_0xb381de){try{for(_0x39ee3b in _0x116012)if(!(_0x2d1504&&_0x448392['test'](_0x39ee3b))&&!this[_0x276822(0x151)](_0x116012,_0x39ee3b,_0x4064e)){if(_0x3e617b++,_0x4064e[_0x276822(0x1b8)]++,_0x3e617b>_0x5bbc59){_0x34b838=!0x0;break;}if(!_0x4064e[_0x276822(0x1c0)]&&_0x4064e['autoExpand']&&_0x4064e[_0x276822(0x1b8)]>_0x4064e[_0x276822(0x14e)]){_0x34b838=!0x0;break;}_0x5988bd[_0x276822(0xf9)](_0x49e6d5[_0x276822(0x117)](_0x22b5a1,_0x1134b4,_0x116012,_0x21d3f8,_0x39ee3b,_0x4064e));}}catch{}if(_0x1134b4['_p_length']=!0x0,_0x2aa0a9&&(_0x1134b4[_0x276822(0x1a6)]=!0x0),!_0x34b838){var _0x46fabb=[][_0x276822(0x1a5)](this[_0x276822(0x17a)](_0x116012))[_0x276822(0x1a5)](this['_getOwnPropertySymbols'](_0x116012));for(_0x409809=0x0,_0x4b11d6=_0x46fabb['length'];_0x409809<_0x4b11d6;_0x409809++)if(_0x39ee3b=_0x46fabb[_0x409809],!(_0x2d1504&&_0x448392[_0x276822(0x16f)](_0x39ee3b[_0x276822(0xff)]()))&&!this[_0x276822(0x151)](_0x116012,_0x39ee3b,_0x4064e)&&!_0x1134b4[_0x276822(0x162)+_0x39ee3b[_0x276822(0xff)]()]){if(_0x3e617b++,_0x4064e['autoExpandPropertyCount']++,_0x3e617b>_0x5bbc59){_0x34b838=!0x0;break;}if(!_0x4064e['isExpressionToEvaluate']&&_0x4064e[_0x276822(0x10b)]&&_0x4064e['autoExpandPropertyCount']>_0x4064e[_0x276822(0x14e)]){_0x34b838=!0x0;break;}_0x5988bd[_0x276822(0xf9)](_0x49e6d5[_0x276822(0x117)](_0x22b5a1,_0x1134b4,_0x116012,_0x21d3f8,_0x39ee3b,_0x4064e));}}}}}if(_0x5416d7[_0x276822(0x123)]=_0x21d3f8,_0x5995ba?(_0x5416d7[_0x276822(0x118)]=_0x116012[_0x276822(0x1ca)](),this[_0x276822(0x1a3)](_0x21d3f8,_0x5416d7,_0x4064e,_0x5d249a)):_0x21d3f8===_0x276822(0x18e)?_0x5416d7[_0x276822(0x118)]=this[_0x276822(0x18f)]['call'](_0x116012):_0x21d3f8===_0x276822(0x1e1)?_0x5416d7[_0x276822(0x118)]=_0x116012[_0x276822(0xff)]():_0x21d3f8===_0x276822(0x1d9)?_0x5416d7[_0x276822(0x118)]=this[_0x276822(0x1db)][_0x276822(0x12d)](_0x116012):_0x21d3f8===_0x276822(0xfb)&&this['_Symbol']?_0x5416d7[_0x276822(0x118)]=this[_0x276822(0x134)]['prototype']['toString'][_0x276822(0x12d)](_0x116012):!_0x4064e[_0x276822(0x125)]&&!(_0x21d3f8===_0x276822(0x1ce)||_0x21d3f8===_0x276822(0x1a9))&&(delete _0x5416d7[_0x276822(0x118)],_0x5416d7[_0x276822(0x15d)]=!0x0),_0x34b838&&(_0x5416d7[_0x276822(0x13b)]=!0x0),_0x3d70f8=_0x4064e[_0x276822(0x1a1)][_0x276822(0x1bc)],_0x4064e[_0x276822(0x1a1)]['current']=_0x5416d7,this[_0x276822(0xf1)](_0x5416d7,_0x4064e),_0x5988bd[_0x276822(0x13f)]){for(_0x409809=0x0,_0x4b11d6=_0x5988bd[_0x276822(0x13f)];_0x409809<_0x4b11d6;_0x409809++)_0x5988bd[_0x409809](_0x409809);}_0x22b5a1[_0x276822(0x13f)]&&(_0x5416d7[_0x276822(0x109)]=_0x22b5a1);}catch(_0x545cfc){_0x3b3f6a(_0x545cfc,_0x5416d7,_0x4064e);}return this[_0x276822(0x132)](_0x116012,_0x5416d7),this[_0x276822(0x103)](_0x5416d7,_0x4064e),_0x4064e['node'][_0x276822(0x1bc)]=_0x3d70f8,_0x4064e[_0x276822(0x171)]--,_0x4064e[_0x276822(0x10b)]=_0x57b7ae,_0x4064e['autoExpand']&&_0x4064e[_0x276822(0x169)][_0x276822(0x1cb)](),_0x5416d7;}[_0x144a79(0x1bf)](_0x342ecf){return Object['getOwnPropertySymbols']?Object['getOwnPropertySymbols'](_0x342ecf):[];}[_0x144a79(0x1c4)](_0x5ba2ae){var _0x5a7687=_0x144a79;return!!(_0x5ba2ae&&_0x3e77c0[_0x5a7687(0xfc)]&&this['_objectToString'](_0x5ba2ae)===_0x5a7687(0x1e0)&&_0x5ba2ae[_0x5a7687(0x1d8)]);}[_0x144a79(0x151)](_0x1cb176,_0x21f6ff,_0x22cda5){var _0x223b84=_0x144a79;return _0x22cda5[_0x223b84(0x11a)]?typeof _0x1cb176[_0x21f6ff]==_0x223b84(0x1df):!0x1;}[_0x144a79(0x1b9)](_0x14fbf5){var _0x540dfe=_0x144a79,_0x21d37e='';return _0x21d37e=typeof _0x14fbf5,_0x21d37e===_0x540dfe(0xf2)?this['_objectToString'](_0x14fbf5)===_0x540dfe(0xef)?_0x21d37e=_0x540dfe(0x12b):this[_0x540dfe(0x1a4)](_0x14fbf5)===_0x540dfe(0x1d0)?_0x21d37e='date':this[_0x540dfe(0x1a4)](_0x14fbf5)===_0x540dfe(0x179)?_0x21d37e=_0x540dfe(0x1e1):_0x14fbf5===null?_0x21d37e=_0x540dfe(0x1ce):_0x14fbf5[_0x540dfe(0x10e)]&&(_0x21d37e=_0x14fbf5['constructor'][_0x540dfe(0xfe)]||_0x21d37e):_0x21d37e===_0x540dfe(0x1a9)&&this[_0x540dfe(0x180)]&&_0x14fbf5 instanceof this['_HTMLAllCollection']&&(_0x21d37e=_0x540dfe(0x157)),_0x21d37e;}[_0x144a79(0x1a4)](_0x8e2c33){var _0x252277=_0x144a79;return Object[_0x252277(0x1e8)]['toString'][_0x252277(0x12d)](_0x8e2c33);}[_0x144a79(0x1e2)](_0x575741){var _0x23d891=_0x144a79;return _0x575741===_0x23d891(0x18a)||_0x575741==='string'||_0x575741===_0x23d891(0x133);}[_0x144a79(0x101)](_0x4456b7){var _0x2de837=_0x144a79;return _0x4456b7===_0x2de837(0x140)||_0x4456b7==='String'||_0x4456b7===_0x2de837(0x135);}[_0x144a79(0xf5)](_0x4354f5,_0x2e6613,_0x5874b5,_0x561ca8,_0x22e9fa,_0xf7af1c){var _0x1cf564=this;return function(_0x384940){var _0x598128=_0x21a3,_0x1c7c0b=_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x1bc)],_0x46c00c=_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x10a)],_0x16be0b=_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x11c)];_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x11c)]=_0x1c7c0b,_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x10a)]=typeof _0x561ca8=='number'?_0x561ca8:_0x384940,_0x4354f5[_0x598128(0xf9)](_0x1cf564['_property'](_0x2e6613,_0x5874b5,_0x561ca8,_0x22e9fa,_0xf7af1c)),_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x11c)]=_0x16be0b,_0x22e9fa['node']['index']=_0x46c00c;};}[_0x144a79(0x117)](_0x58d056,_0x3f3d81,_0x423f15,_0x7eaa6e,_0xd8e80,_0x57780f,_0x4dffbc){var _0x32a146=_0x144a79,_0x41efa8=this;return _0x3f3d81[_0x32a146(0x162)+_0xd8e80['toString']()]=!0x0,function(_0x4d920b){var _0x49b5c2=_0x32a146,_0x5ac13c=_0x57780f[_0x49b5c2(0x1a1)][_0x49b5c2(0x1bc)],_0x3b2e7f=_0x57780f['node'][_0x49b5c2(0x10a)],_0x188cf3=_0x57780f[_0x49b5c2(0x1a1)]['parent'];_0x57780f[_0x49b5c2(0x1a1)][_0x49b5c2(0x11c)]=_0x5ac13c,_0x57780f['node'][_0x49b5c2(0x10a)]=_0x4d920b,_0x58d056[_0x49b5c2(0xf9)](_0x41efa8[_0x49b5c2(0x11e)](_0x423f15,_0x7eaa6e,_0xd8e80,_0x57780f,_0x4dffbc)),_0x57780f[_0x49b5c2(0x1a1)][_0x49b5c2(0x11c)]=_0x188cf3,_0x57780f[_0x49b5c2(0x1a1)][_0x49b5c2(0x10a)]=_0x3b2e7f;};}[_0x144a79(0x11e)](_0x30d594,_0x313c60,_0x116b3f,_0x110528,_0x4c9ac0){var _0x860bad=_0x144a79,_0x3c61c2=this;_0x4c9ac0||(_0x4c9ac0=function(_0x374fcf,_0x341fdd){return _0x374fcf[_0x341fdd];});var _0x5ebc8c=_0x116b3f[_0x860bad(0xff)](),_0x32f673=_0x110528[_0x860bad(0x181)]||{},_0x38968e=_0x110528['depth'],_0xd7277d=_0x110528[_0x860bad(0x1c0)];try{var _0x14b85f=this['_isMap'](_0x30d594),_0x316f78=_0x5ebc8c;_0x14b85f&&_0x316f78[0x0]==='\\x27'&&(_0x316f78=_0x316f78[_0x860bad(0x112)](0x1,_0x316f78['length']-0x2));var _0x4099d8=_0x110528[_0x860bad(0x181)]=_0x32f673['_p_'+_0x316f78];_0x4099d8&&(_0x110528[_0x860bad(0x125)]=_0x110528[_0x860bad(0x125)]+0x1),_0x110528[_0x860bad(0x1c0)]=!!_0x4099d8;var _0x28f7fd=typeof _0x116b3f==_0x860bad(0xfb),_0x33a0d0={'name':_0x28f7fd||_0x14b85f?_0x5ebc8c:this[_0x860bad(0x1cf)](_0x5ebc8c)};if(_0x28f7fd&&(_0x33a0d0[_0x860bad(0xfb)]=!0x0),!(_0x313c60===_0x860bad(0x12b)||_0x313c60==='Error')){var _0x1db4a5=this[_0x860bad(0x1c2)](_0x30d594,_0x116b3f);if(_0x1db4a5&&(_0x1db4a5[_0x860bad(0x188)]&&(_0x33a0d0[_0x860bad(0x1d4)]=!0x0),_0x1db4a5[_0x860bad(0x136)]&&!_0x4099d8&&!_0x110528[_0x860bad(0x17b)]))return _0x33a0d0[_0x860bad(0x11f)]=!0x0,this['_processTreeNodeResult'](_0x33a0d0,_0x110528),_0x33a0d0;}var _0x45eef2;try{_0x45eef2=_0x4c9ac0(_0x30d594,_0x116b3f);}catch(_0x349276){return _0x33a0d0={'name':_0x5ebc8c,'type':'unknown','error':_0x349276[_0x860bad(0x1ae)]},this[_0x860bad(0x120)](_0x33a0d0,_0x110528),_0x33a0d0;}var _0x539394=this[_0x860bad(0x1b9)](_0x45eef2),_0x490c82=this[_0x860bad(0x1e2)](_0x539394);if(_0x33a0d0[_0x860bad(0x123)]=_0x539394,_0x490c82)this[_0x860bad(0x120)](_0x33a0d0,_0x110528,_0x45eef2,function(){var _0x5a3749=_0x860bad;_0x33a0d0[_0x5a3749(0x118)]=_0x45eef2['valueOf'](),!_0x4099d8&&_0x3c61c2[_0x5a3749(0x1a3)](_0x539394,_0x33a0d0,_0x110528,{});});else{var _0x3ce9b1=_0x110528['autoExpand']&&_0x110528[_0x860bad(0x171)]<_0x110528[_0x860bad(0x1cd)]&&_0x110528[_0x860bad(0x169)][_0x860bad(0x111)](_0x45eef2)<0x0&&_0x539394!=='function'&&_0x110528['autoExpandPropertyCount']<_0x110528[_0x860bad(0x14e)];_0x3ce9b1||_0x110528[_0x860bad(0x171)]<_0x38968e||_0x4099d8?(this[_0x860bad(0x15e)](_0x33a0d0,_0x45eef2,_0x110528,_0x4099d8||{}),this[_0x860bad(0x132)](_0x45eef2,_0x33a0d0)):this[_0x860bad(0x120)](_0x33a0d0,_0x110528,_0x45eef2,function(){var _0x4e871a=_0x860bad;_0x539394===_0x4e871a(0x1ce)||_0x539394===_0x4e871a(0x1a9)||(delete _0x33a0d0[_0x4e871a(0x118)],_0x33a0d0['capped']=!0x0);});}return _0x33a0d0;}finally{_0x110528[_0x860bad(0x181)]=_0x32f673,_0x110528['depth']=_0x38968e,_0x110528[_0x860bad(0x1c0)]=_0xd7277d;}}[_0x144a79(0x1a3)](_0x48037b,_0x2f1dd0,_0x473c43,_0x2ec293){var _0x39eca7=_0x144a79,_0x3f171f=_0x2ec293[_0x39eca7(0x139)]||_0x473c43[_0x39eca7(0x139)];if((_0x48037b==='string'||_0x48037b===_0x39eca7(0xf3))&&_0x2f1dd0[_0x39eca7(0x118)]){let _0x2d465d=_0x2f1dd0[_0x39eca7(0x118)][_0x39eca7(0x13f)];_0x473c43[_0x39eca7(0x178)]+=_0x2d465d,_0x473c43[_0x39eca7(0x178)]>_0x473c43[_0x39eca7(0x1d2)]?(_0x2f1dd0[_0x39eca7(0x15d)]='',delete _0x2f1dd0['value']):_0x2d465d>_0x3f171f&&(_0x2f1dd0[_0x39eca7(0x15d)]=_0x2f1dd0[_0x39eca7(0x118)][_0x39eca7(0x112)](0x0,_0x3f171f),delete _0x2f1dd0[_0x39eca7(0x118)]);}}[_0x144a79(0x176)](_0x5e12a9){var _0x2e6805=_0x144a79;return!!(_0x5e12a9&&_0x3e77c0[_0x2e6805(0x149)]&&this[_0x2e6805(0x1a4)](_0x5e12a9)===_0x2e6805(0x1e6)&&_0x5e12a9[_0x2e6805(0x1d8)]);}[_0x144a79(0x1cf)](_0x45972a){var _0x58ae9d=_0x144a79;if(_0x45972a[_0x58ae9d(0x19b)](/^\\d+$/))return _0x45972a;var _0x319850;try{_0x319850=JSON[_0x58ae9d(0x1bb)](''+_0x45972a);}catch{_0x319850='\\x22'+this[_0x58ae9d(0x1a4)](_0x45972a)+'\\x22';}return _0x319850[_0x58ae9d(0x19b)](/^\"([a-zA-Z_][a-zA-Z_0-9]*)\"$/)?_0x319850=_0x319850[_0x58ae9d(0x112)](0x1,_0x319850['length']-0x2):_0x319850=_0x319850['replace'](/'/g,'\\x5c\\x27')[_0x58ae9d(0x144)](/\\\\\"/g,'\\x22')[_0x58ae9d(0x144)](/(^\"|\"$)/g,'\\x27'),_0x319850;}[_0x144a79(0x120)](_0x1bd8dc,_0x2ed4bd,_0x324def,_0x4e2bae){var _0x357ebc=_0x144a79;this[_0x357ebc(0xf1)](_0x1bd8dc,_0x2ed4bd),_0x4e2bae&&_0x4e2bae(),this[_0x357ebc(0x132)](_0x324def,_0x1bd8dc),this[_0x357ebc(0x103)](_0x1bd8dc,_0x2ed4bd);}['_treeNodePropertiesBeforeFullValue'](_0x107d65,_0x3ed7ca){var _0x219b16=_0x144a79;this['_setNodeId'](_0x107d65,_0x3ed7ca),this[_0x219b16(0x124)](_0x107d65,_0x3ed7ca),this[_0x219b16(0x1ac)](_0x107d65,_0x3ed7ca),this[_0x219b16(0x110)](_0x107d65,_0x3ed7ca);}[_0x144a79(0x147)](_0x56ce50,_0x58285a){}[_0x144a79(0x124)](_0x2fd389,_0x113b19){}[_0x144a79(0x10d)](_0x46fedc,_0x1fb3d1){}[_0x144a79(0x1c8)](_0x53d6a4){var _0x314f4b=_0x144a79;return _0x53d6a4===this[_0x314f4b(0x168)];}[_0x144a79(0x103)](_0x94f0d4,_0x1f78a2){var _0xc416c9=_0x144a79;this[_0xc416c9(0x10d)](_0x94f0d4,_0x1f78a2),this['_setNodeExpandableState'](_0x94f0d4),_0x1f78a2['sortProps']&&this[_0xc416c9(0xee)](_0x94f0d4),this[_0xc416c9(0x100)](_0x94f0d4,_0x1f78a2),this[_0xc416c9(0x19a)](_0x94f0d4,_0x1f78a2),this[_0xc416c9(0x115)](_0x94f0d4);}[_0x144a79(0x132)](_0x912632,_0x5f5544){var _0x20b522=_0x144a79;let _0x50f594;try{_0x3e77c0[_0x20b522(0x154)]&&(_0x50f594=_0x3e77c0[_0x20b522(0x154)][_0x20b522(0x174)],_0x3e77c0[_0x20b522(0x154)][_0x20b522(0x174)]=function(){}),_0x912632&&typeof _0x912632['length']==_0x20b522(0x133)&&(_0x5f5544['length']=_0x912632['length']);}catch{}finally{_0x50f594&&(_0x3e77c0[_0x20b522(0x154)]['error']=_0x50f594);}if(_0x5f5544[_0x20b522(0x123)]==='number'||_0x5f5544[_0x20b522(0x123)]===_0x20b522(0x135)){if(isNaN(_0x5f5544[_0x20b522(0x118)]))_0x5f5544[_0x20b522(0x158)]=!0x0,delete _0x5f5544['value'];else switch(_0x5f5544[_0x20b522(0x118)]){case Number['POSITIVE_INFINITY']:_0x5f5544[_0x20b522(0x186)]=!0x0,delete _0x5f5544[_0x20b522(0x118)];break;case Number[_0x20b522(0x116)]:_0x5f5544[_0x20b522(0x1aa)]=!0x0,delete _0x5f5544[_0x20b522(0x118)];break;case 0x0:this[_0x20b522(0x141)](_0x5f5544[_0x20b522(0x118)])&&(_0x5f5544[_0x20b522(0x14c)]=!0x0);break;}}else _0x5f5544[_0x20b522(0x123)]===_0x20b522(0x1df)&&typeof _0x912632['name']==_0x20b522(0x13d)&&_0x912632[_0x20b522(0xfe)]&&_0x5f5544[_0x20b522(0xfe)]&&_0x912632[_0x20b522(0xfe)]!==_0x5f5544['name']&&(_0x5f5544[_0x20b522(0x196)]=_0x912632['name']);}[_0x144a79(0x141)](_0x3d8de7){var _0x5bd0fa=_0x144a79;return 0x1/_0x3d8de7===Number[_0x5bd0fa(0x116)];}['_sortProps'](_0x4aa6dd){var _0x119666=_0x144a79;!_0x4aa6dd[_0x119666(0x109)]||!_0x4aa6dd[_0x119666(0x109)][_0x119666(0x13f)]||_0x4aa6dd[_0x119666(0x123)]===_0x119666(0x12b)||_0x4aa6dd[_0x119666(0x123)]===_0x119666(0x149)||_0x4aa6dd[_0x119666(0x123)]===_0x119666(0xfc)||_0x4aa6dd[_0x119666(0x109)]['sort'](function(_0x31ad1b,_0x1e1fbf){var _0x2d660e=_0x119666,_0x1d39c7=_0x31ad1b[_0x2d660e(0xfe)][_0x2d660e(0x1ab)](),_0x58bbc5=_0x1e1fbf['name'][_0x2d660e(0x1ab)]();return _0x1d39c7<_0x58bbc5?-0x1:_0x1d39c7>_0x58bbc5?0x1:0x0;});}[_0x144a79(0x100)](_0x256f87,_0x39cae1){var _0x3d0616=_0x144a79;if(!(_0x39cae1[_0x3d0616(0x11a)]||!_0x256f87['props']||!_0x256f87['props'][_0x3d0616(0x13f)])){for(var _0x12a78b=[],_0x11c382=[],_0x564eb4=0x0,_0x128043=_0x256f87['props'][_0x3d0616(0x13f)];_0x564eb4<_0x128043;_0x564eb4++){var _0x5b20c3=_0x256f87[_0x3d0616(0x109)][_0x564eb4];_0x5b20c3[_0x3d0616(0x123)]==='function'?_0x12a78b[_0x3d0616(0xf9)](_0x5b20c3):_0x11c382[_0x3d0616(0xf9)](_0x5b20c3);}if(!(!_0x11c382[_0x3d0616(0x13f)]||_0x12a78b['length']<=0x1)){_0x256f87[_0x3d0616(0x109)]=_0x11c382;var _0x32daf8={'functionsNode':!0x0,'props':_0x12a78b};this[_0x3d0616(0x147)](_0x32daf8,_0x39cae1),this[_0x3d0616(0x10d)](_0x32daf8,_0x39cae1),this['_setNodeExpandableState'](_0x32daf8),this[_0x3d0616(0x110)](_0x32daf8,_0x39cae1),_0x32daf8['id']+='\\x20f',_0x256f87[_0x3d0616(0x109)][_0x3d0616(0x1b7)](_0x32daf8);}}}[_0x144a79(0x19a)](_0x15acc0,_0x155efd){}[_0x144a79(0x161)](_0x2e282a){}[_0x144a79(0x18b)](_0x237103){var _0xfedee4=_0x144a79;return Array[_0xfedee4(0x164)](_0x237103)||typeof _0x237103==_0xfedee4(0xf2)&&this['_objectToString'](_0x237103)===_0xfedee4(0xef);}[_0x144a79(0x110)](_0x93d6b1,_0x555532){}[_0x144a79(0x115)](_0x2328ec){var _0x443c04=_0x144a79;delete _0x2328ec[_0x443c04(0x1c9)],delete _0x2328ec[_0x443c04(0x106)],delete _0x2328ec[_0x443c04(0x1c6)];}[_0x144a79(0x1ac)](_0x473e8a,_0x1f3de2){}}let _0x182186=new _0x4e138c(),_0x4c7196={'props':0x64,'elements':0x64,'strLength':0x400*0x32,'totalStrLength':0x400*0x32,'autoExpandLimit':0x1388,'autoExpandMaxDepth':0xa},_0x3701fa={'props':0x5,'elements':0x5,'strLength':0x100,'totalStrLength':0x100*0x3,'autoExpandLimit':0x1e,'autoExpandMaxDepth':0x2};function _0x213e3e(_0x2ff700,_0x1028d3,_0x6237b8,_0x3512a1,_0xcb090,_0x3b6c4b){var _0x407e31=_0x144a79;let _0x212837,_0x4e347f;try{_0x4e347f=_0x24d130(),_0x212837=_0x3d2d4c[_0x1028d3],!_0x212837||_0x4e347f-_0x212837['ts']>0x1f4&&_0x212837[_0x407e31(0x197)]&&_0x212837[_0x407e31(0x146)]/_0x212837[_0x407e31(0x197)]<0x64?(_0x3d2d4c[_0x1028d3]=_0x212837={'count':0x0,'time':0x0,'ts':_0x4e347f},_0x3d2d4c[_0x407e31(0x1c7)]={}):_0x4e347f-_0x3d2d4c['hits']['ts']>0x32&&_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x197)]&&_0x3d2d4c['hits'][_0x407e31(0x146)]/_0x3d2d4c[_0x407e31(0x1c7)]['count']<0x64&&(_0x3d2d4c[_0x407e31(0x1c7)]={});let _0x36a3f2=[],_0x24554b=_0x212837[_0x407e31(0x102)]||_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x102)]?_0x3701fa:_0x4c7196,_0x347fd7=_0x261a7d=>{var _0x3edcb4=_0x407e31;let _0x3a2713={};return _0x3a2713['props']=_0x261a7d['props'],_0x3a2713[_0x3edcb4(0x122)]=_0x261a7d[_0x3edcb4(0x122)],_0x3a2713[_0x3edcb4(0x139)]=_0x261a7d[_0x3edcb4(0x139)],_0x3a2713[_0x3edcb4(0x1d2)]=_0x261a7d[_0x3edcb4(0x1d2)],_0x3a2713[_0x3edcb4(0x14e)]=_0x261a7d[_0x3edcb4(0x14e)],_0x3a2713[_0x3edcb4(0x1cd)]=_0x261a7d[_0x3edcb4(0x1cd)],_0x3a2713[_0x3edcb4(0xf6)]=!0x1,_0x3a2713[_0x3edcb4(0x11a)]=!_0x368502,_0x3a2713[_0x3edcb4(0x125)]=0x1,_0x3a2713[_0x3edcb4(0x171)]=0x0,_0x3a2713[_0x3edcb4(0x15b)]=_0x3edcb4(0x1dc),_0x3a2713[_0x3edcb4(0x1a0)]='root_exp',_0x3a2713[_0x3edcb4(0x10b)]=!0x0,_0x3a2713['autoExpandPreviousObjects']=[],_0x3a2713[_0x3edcb4(0x1b8)]=0x0,_0x3a2713[_0x3edcb4(0x17b)]=!0x0,_0x3a2713[_0x3edcb4(0x178)]=0x0,_0x3a2713[_0x3edcb4(0x1a1)]={'current':void 0x0,'parent':void 0x0,'index':0x0},_0x3a2713;};for(var _0x25383a=0x0;_0x25383a<_0xcb090[_0x407e31(0x13f)];_0x25383a++)_0x36a3f2[_0x407e31(0xf9)](_0x182186['serialize']({'timeNode':_0x2ff700===_0x407e31(0x146)||void 0x0},_0xcb090[_0x25383a],_0x347fd7(_0x24554b),{}));if(_0x2ff700==='trace'||_0x2ff700===_0x407e31(0x174)){let _0x5a1a14=Error[_0x407e31(0xfa)];try{Error[_0x407e31(0xfa)]=0x1/0x0,_0x36a3f2[_0x407e31(0xf9)](_0x182186[_0x407e31(0x15e)]({'stackNode':!0x0},new Error()[_0x407e31(0x15f)],_0x347fd7(_0x24554b),{'strLength':0x1/0x0}));}finally{Error[_0x407e31(0xfa)]=_0x5a1a14;}}return{'method':_0x407e31(0x194),'version':_0x391fb4,'args':[{'ts':_0x6237b8,'session':_0x3512a1,'args':_0x36a3f2,'id':_0x1028d3,'context':_0x3b6c4b}]};}catch(_0x5db04b){return{'method':_0x407e31(0x194),'version':_0x391fb4,'args':[{'ts':_0x6237b8,'session':_0x3512a1,'args':[{'type':_0x407e31(0x1b6),'error':_0x5db04b&&_0x5db04b[_0x407e31(0x1ae)]}],'id':_0x1028d3,'context':_0x3b6c4b}]};}finally{try{if(_0x212837&&_0x4e347f){let _0x45e299=_0x24d130();_0x212837['count']++,_0x212837[_0x407e31(0x146)]+=_0x47a395(_0x4e347f,_0x45e299),_0x212837['ts']=_0x45e299,_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x197)]++,_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x146)]+=_0x47a395(_0x4e347f,_0x45e299),_0x3d2d4c[_0x407e31(0x1c7)]['ts']=_0x45e299,(_0x212837[_0x407e31(0x197)]>0x32||_0x212837[_0x407e31(0x146)]>0x64)&&(_0x212837['reduceLimits']=!0x0),(_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x197)]>0x3e8||_0x3d2d4c[_0x407e31(0x1c7)]['time']>0x12c)&&(_0x3d2d4c[_0x407e31(0x1c7)]['reduceLimits']=!0x0);}}catch{}}}return _0x213e3e;}((_0x5d65e9,_0x571af9,_0x37e1e4,_0x433d48,_0x1b02f6,_0x430083,_0x5432d8,_0x102dcb,_0x493a3c,_0x240da5,_0x2821b9)=>{var _0x556dd6=_0x2160de;if(_0x5d65e9[_0x556dd6(0x14d)])return _0x5d65e9[_0x556dd6(0x14d)];if(!H(_0x5d65e9,_0x102dcb,_0x1b02f6))return _0x5d65e9[_0x556dd6(0x14d)]={'consoleLog':()=>{},'consoleTrace':()=>{},'consoleTime':()=>{},'consoleTimeEnd':()=>{},'autoLog':()=>{},'autoLogMany':()=>{},'autoTraceMany':()=>{},'coverage':()=>{},'autoTrace':()=>{},'autoTime':()=>{},'autoTimeEnd':()=>{}},_0x5d65e9['_console_ninja'];let _0x1d6340=B(_0x5d65e9),_0x397ff6=_0x1d6340['elapsed'],_0x1a91da=_0x1d6340['timeStamp'],_0xa22aa9=_0x1d6340[_0x556dd6(0x129)],_0x4dd46f={'hits':{},'ts':{}},_0x22fa43=X(_0x5d65e9,_0x493a3c,_0x4dd46f,_0x430083),_0x590e5e=_0x44c539=>{_0x4dd46f['ts'][_0x44c539]=_0x1a91da();},_0x1ecc6b=(_0x392121,_0x1d046a)=>{let _0x489bc2=_0x4dd46f['ts'][_0x1d046a];if(delete _0x4dd46f['ts'][_0x1d046a],_0x489bc2){let _0x346578=_0x397ff6(_0x489bc2,_0x1a91da());_0x37b780(_0x22fa43('time',_0x392121,_0xa22aa9(),_0x4ee1c5,[_0x346578],_0x1d046a));}},_0x11ac60=_0x3097ff=>{var _0x43212c=_0x556dd6,_0x5de73e;return _0x1b02f6===_0x43212c(0x1a7)&&_0x5d65e9[_0x43212c(0xf8)]&&((_0x5de73e=_0x3097ff==null?void 0x0:_0x3097ff['args'])==null?void 0x0:_0x5de73e['length'])&&(_0x3097ff[_0x43212c(0x114)][0x0]['origin']=_0x5d65e9[_0x43212c(0xf8)]),_0x3097ff;};_0x5d65e9[_0x556dd6(0x14d)]={'consoleLog':(_0x3df3db,_0x1bb5ab)=>{var _0x2864ec=_0x556dd6;_0x5d65e9[_0x2864ec(0x154)][_0x2864ec(0x194)][_0x2864ec(0xfe)]!==_0x2864ec(0x119)&&_0x37b780(_0x22fa43(_0x2864ec(0x194),_0x3df3db,_0xa22aa9(),_0x4ee1c5,_0x1bb5ab));},'consoleTrace':(_0x29da49,_0x3f8374)=>{var _0x280bf8=_0x556dd6,_0x2254c1,_0x1e4130;_0x5d65e9['console'][_0x280bf8(0x194)][_0x280bf8(0xfe)]!=='disabledTrace'&&((_0x1e4130=(_0x2254c1=_0x5d65e9[_0x280bf8(0x1d1)])==null?void 0x0:_0x2254c1['versions'])!=null&&_0x1e4130[_0x280bf8(0x1a1)]&&(_0x5d65e9[_0x280bf8(0x1de)]=!0x0),_0x37b780(_0x11ac60(_0x22fa43(_0x280bf8(0x170),_0x29da49,_0xa22aa9(),_0x4ee1c5,_0x3f8374))));},'consoleError':(_0x547f0a,_0x50dbc7)=>{var _0x3597a9=_0x556dd6;_0x5d65e9[_0x3597a9(0x1de)]=!0x0,_0x37b780(_0x11ac60(_0x22fa43('error',_0x547f0a,_0xa22aa9(),_0x4ee1c5,_0x50dbc7)));},'consoleTime':_0x123ffe=>{_0x590e5e(_0x123ffe);},'consoleTimeEnd':(_0x2171a0,_0x403bb8)=>{_0x1ecc6b(_0x403bb8,_0x2171a0);},'autoLog':(_0x4998d0,_0x1bac37)=>{var _0xd73d23=_0x556dd6;_0x37b780(_0x22fa43(_0xd73d23(0x194),_0x1bac37,_0xa22aa9(),_0x4ee1c5,[_0x4998d0]));},'autoLogMany':(_0x431d18,_0x36d175)=>{var _0x73d5fc=_0x556dd6;_0x37b780(_0x22fa43(_0x73d5fc(0x194),_0x431d18,_0xa22aa9(),_0x4ee1c5,_0x36d175));},'autoTrace':(_0x459c49,_0x27ff2b)=>{var _0x433b06=_0x556dd6;_0x37b780(_0x11ac60(_0x22fa43(_0x433b06(0x170),_0x27ff2b,_0xa22aa9(),_0x4ee1c5,[_0x459c49])));},'autoTraceMany':(_0x2ff4f7,_0x18ce27)=>{_0x37b780(_0x11ac60(_0x22fa43('trace',_0x2ff4f7,_0xa22aa9(),_0x4ee1c5,_0x18ce27)));},'autoTime':(_0x2e41bc,_0x5eb8ec,_0x1c38dd)=>{_0x590e5e(_0x1c38dd);},'autoTimeEnd':(_0x47c008,_0xad9b13,_0x4fc532)=>{_0x1ecc6b(_0xad9b13,_0x4fc532);},'coverage':_0x4de91b=>{_0x37b780({'method':'coverage','version':_0x430083,'args':[{'id':_0x4de91b}]});}};let _0x37b780=q(_0x5d65e9,_0x571af9,_0x37e1e4,_0x433d48,_0x1b02f6,_0x240da5,_0x2821b9),_0x4ee1c5=_0x5d65e9[_0x556dd6(0x14a)];return _0x5d65e9['_console_ninja'];})(globalThis,_0x2160de(0x187),'50437',_0x2160de(0x16b),_0x2160de(0x1be),_0x2160de(0x1e3),_0x2160de(0x128),_0x2160de(0x1d5),_0x2160de(0x159),_0x2160de(0x183),_0x2160de(0x152));");}catch(e){}};/* istanbul ignore next */function oo_oo(i:string,...v:any[]){try{oo_cm().consoleLog(i, v);}catch(e){} return v};oo_oo;/* istanbul ignore next */function oo_tr(i:string,...v:any[]){try{oo_cm().consoleTrace(i, v);}catch(e){} return v};oo_tr;/* istanbul ignore next */function oo_tx(i:string,...v:any[]){try{oo_cm().consoleError(i, v);}catch(e){} return v};oo_tx;/* istanbul ignore next */function oo_ts(v?:string):string{try{oo_cm().consoleTime(v);}catch(e){} return v as string;};oo_ts;/* istanbul ignore next */function oo_te(v:string|undefined, i:string):string{try{oo_cm().consoleTimeEnd(v, i);}catch(e){} return v as string;};oo_te;/*eslint unicorn/no-abusive-eslint-disable:,eslint-comments/disable-enable-pair:,eslint-comments/no-unlimited-disable:,eslint-comments/no-aggregating-enable:,eslint-comments/no-duplicate-disable:,eslint-comments/no-unused-disable:,eslint-comments/no-unused-enable:,*/